import { useState, useCallback, useRef, useEffect, useMemo, memo } from "react";
import { API_BASE, submitQuery, pollTask, downloadReport, healthCheck, fetchDatasources, FALLBACK_DATASOURCES, type TaskStatus, type DataSourceProgress as DSProgressType, type DatasourceConfig, type DatasourcesResponse } from "./lib/api";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import { useQueryHistory } from "./hooks/useQueryHistory";
import DosageFormDropdown, { type DosageForm, DOSAGE_FORMS } from "./components/DosageFormDropdown";
import DrugInputWithSuggestions from "./components/DrugInputWithSuggestions";
import { type DosageFormMatchResult } from "./services/dosageFormExtractor";
import SpecificationInput from "./components/SpecificationInput";
import QueryHistoryList from "./components/QueryHistoryList";
import DownloadPanel from "./components/DownloadPanel";
import ProgressGrid, { type ProgressGridSource, DATASOURCE_NAMES, DATASOURCE_REGIONS, DATASOURCE_ICONS } from "./components/ProgressGrid";
import SummaryPreviewCard, { type SummaryPreviewCardProps } from "./components/SummaryPreviewCard";
import GlobalLoading from "./components/GlobalLoading";
import UserFriendlyError, { categorizeError } from "./components/UserFriendlyError";
import { type DrugMapping, drugMappings } from "./lib/drugMappings";
import { validateSpecification } from "./lib/specification";
import { alignMarkdownRow, parseMarkdownRow } from "./lib/markdownTable";
import { buildCsv, getSourceUrl, safeDownloadName } from "./lib/resultActions";
import { Atom, ExternalLink, FileText } from "lucide-react";

type Stage = "submit" | "progress" | "result";
type ResultTab = "all" | "success" | "issues";

function formatDataForDisplay(data: any): string {
  if (data === null || data === undefined) {
    return '';
  }
  
  if (typeof data === 'object' && data !== null) {
    try {
      return JSON.stringify(data, null, 2);
    } catch (e) {
      return String(data);
    }
  }
  
  if (Array.isArray(data)) {
    return data.join(', ');
  }
  
  return String(data);
}

interface FormErrors {
  drugName?: string;
  dosageForm?: string;
  specification?: string;
  datasources?: string;
}

function DrugChip({ drug }: { drug: DrugMapping | null }) {
  if (!drug) return null;
  return (
    <span className="inline-flex items-baseline gap-2">
      <span className="font-semibold text-foreground">{drug.nameZh}</span>
      <span className="text-sm text-muted-foreground">({drug.nameEn})</span>
    </span>
  );
}

function toDrugMapping(drug: Partial<DrugMapping> & { id: string; nameZh: string; nameEn: string }): DrugMapping {
  return {
    id: drug.id,
    nameZh: drug.nameZh,
    nameEn: drug.nameEn,
    inn: drug.inn || drug.nameEn,
    aliasesZh: drug.aliasesZh || [],
    aliasesEn: drug.aliasesEn || [],
    pinyin: drug.pinyin || [],
    pinyinAbbr: drug.pinyinAbbr || "",
    category: drug.category || "",
    therapeuticClass: drug.therapeuticClass || "",
  };
}

function ReferenceDrugTable({ drugs }: { drugs: Record<string, unknown>[] }) {
  if (!drugs || drugs.length === 0) return null;
  const sourceColorMap: Record<string, string> = {
    drugfuture: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    yaozh: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
    builtin: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    builtin_file: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
  };
  const sourceLabelMap: Record<string, string> = {
    drugfuture: "drugfuture",
    yaozh: "药智网",
    builtin: "内置缓存",
    builtin_file: "文件缓存",
  };
  return (
    <div className="mt-3 overflow-x-auto rounded-lg border border-border">
      <table className="min-w-full text-xs sm:text-sm">
        <thead>
          <tr className="bg-muted/50 sticky top-0">
            <th className="px-2 py-2.5 text-left font-medium text-muted-foreground whitespace-nowrap">#</th>
            <th className="px-2 py-2.5 text-left font-medium text-foreground whitespace-nowrap">药品名称</th>
            <th className="px-2 py-2.5 text-left font-medium text-foreground whitespace-nowrap hidden md:table-cell">英文名称</th>
            <th className="px-2 py-2.5 text-center font-medium text-foreground whitespace-nowrap">剂型</th>
            <th className="px-2 py-2.5 text-left font-medium text-foreground whitespace-nowrap">规格</th>
            <th className="px-2 py-2.5 text-left font-medium text-foreground whitespace-nowrap hidden lg:table-cell">持证商</th>
            <th className="px-2 py-2.5 text-left font-medium text-foreground whitespace-nowrap hidden xl:table-cell">备注</th>
            <th className="px-2 py-2.5 text-center font-medium text-foreground whitespace-nowrap">状态</th>
            <th className="px-2 py-2.5 text-center font-medium text-foreground whitespace-nowrap">来源</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {drugs.map((drug, idx) => {
            const src = String(drug.source || drug.source_name || "—");
            const matchType = String(drug.match_type || "exact");
            const status = String(drug.approval_status || "—");
            const isRef = status === "正式通稿";
            return (
              <tr key={idx} className={`hover:bg-muted/30 transition-colors ${matchType === "reference" ? "opacity-70" : ""}`}>
                <td className="px-2 py-2 text-muted-foreground">{idx + 1}</td>
                <td className="px-2 py-2 font-medium text-foreground max-w-[160px] truncate" title={String(drug.drug_name || "")}>
                  {String(drug.drug_name || "—")}
                </td>
                <td className="px-2 py-2 text-muted-foreground max-w-[180px] truncate hidden md:table-cell" title={String(drug.english_name || "")}>
                  {String(drug.english_name || "—")}
                </td>
                <td className="px-2 py-2 text-center">
                  <span className="inline-block px-1.5 py-0.5 rounded bg-secondary text-secondary-foreground text-xs">
                    {String(drug.dosage_form || "—")}
                  </span>
                </td>
                <td className="px-2 py-2 text-foreground font-mono text-xs">{String(drug.specification || "—")}</td>
                <td className="px-2 py-2 text-muted-foreground max-w-[180px] truncate hidden lg:table-cell" title={String(drug.holder || "")}>
                  {String(drug.holder || "—")}
                </td>
                <td className="px-2 py-2 text-muted-foreground max-w-[150px] truncate hidden xl:table-cell" title={String(drug.source_type || "")}>
                  {String(drug.source_type || "—").slice(0, 20)}
                </td>
                <td className="px-2 py-2 text-center">
                  {isRef ? (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-success/10 text-success text-xs font-medium">
                      ✓ 正式
                    </span>
                  ) : status !== "—" && status !== "" ? (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-warning/10 text-warning text-xs">
                      {status.slice(0, 4)}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-2 py-2 text-center">
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${sourceColorMap[src] || "bg-gray-100 text-gray-600"}`}>
                    {sourceLabelMap[src] || src}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function formatFdaDate(value: unknown): string {
  const text = String(value || "");
  return /^\d{8}$/.test(text)
    ? `${text.slice(0, 4)}-${text.slice(4, 6)}-${text.slice(6, 8)}`
    : text || "-";
}

export function FdaReviewDocuments({ review }: { review: Record<string, unknown> }) {
  const applications = Array.isArray(review.applications)
    ? review.applications as Record<string, unknown>[]
    : [];
  const targetDocuments = Array.isArray(review.target_review_pdfs)
    ? review.target_review_pdfs as Record<string, unknown>[]
    : [];
  const documents = targetDocuments.length > 0
    ? targetDocuments
    : Array.isArray(review.review_documents)
    ? review.review_documents as Record<string, unknown>[]
    : [];
  const displayApplications = applications.length > 0
    ? applications
    : Array.from(new Set(documents.map((document) =>
        String(document.application_number || ""),
      )))
        .filter(Boolean)
        .map((applicationNumber) => ({
          application_number: applicationNumber,
          overview_url: "",
          brand_names: [],
          active_ingredients: [],
          sponsor_name: "",
        }));

  if (applications.length === 0 && documents.length === 0) return null;

  return (
    <div className="mt-4 divide-y divide-border border-y border-border">
      {displayApplications.map((application) => {
        const applicationNumber = String(application.application_number || "");
        const overviewUrl = String(application.overview_url || "");
        const brands = Array.isArray(application.brand_names)
          ? application.brand_names.map(String).filter(Boolean)
          : [];
        const ingredients = Array.isArray(application.active_ingredients)
          ? application.active_ingredients.map(String).filter(Boolean)
          : [];
        const sponsor = String(application.sponsor_name || "");
        const applicationDocuments = documents.filter(
          (document) => String(document.application_number || "") === applicationNumber,
        );
        const pdfDocuments = applicationDocuments.filter((document) => Boolean(document.is_pdf));
        const visibleDocuments = pdfDocuments.length > 0
          ? pdfDocuments
          : applicationDocuments;

        return (
          <section key={applicationNumber || overviewUrl} className="py-4 first:pt-0 last:pb-0">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-sm font-semibold text-foreground">
                    {applicationNumber || "FDA 申请"}
                  </span>
                  {brands.length > 0 && (
                    <span className="text-sm text-foreground">{brands.join(" / ")}</span>
                  )}
                </div>
                {(ingredients.length > 0 || sponsor) && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {[ingredients.join(" / "), sponsor].filter(Boolean).join(" · ")}
                  </p>
                )}
              </div>
              {overviewUrl && (
                <a
                  href={overviewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex shrink-0 items-center gap-1.5 text-xs text-primary hover:underline"
                >
                  查看申请页
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
              )}
            </div>

            {visibleDocuments.length > 0 ? (
              <div className="mt-3 overflow-x-auto">
                <table className="min-w-[720px] w-full text-xs">
                  <thead>
                    <tr className="border-b border-border text-muted-foreground">
                      <th className="px-2 py-2 text-left font-medium">Review 类型</th>
                      <th className="px-2 py-2 text-left font-medium">文档</th>
                      <th className="px-2 py-2 text-left font-medium">日期</th>
                      <th className="px-2 py-2 text-right font-medium">操作</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/70">
                    {visibleDocuments.map((document, index) => {
                      const url = String(document.url || "");
                      const isPdf = Boolean(document.is_pdf);
                      const displayName = String(
                        document.display_name ||
                        document.file_name ||
                        document.title ||
                        document.document_type ||
                        "FDA Review",
                      );
                      const downloadName = isPdf
                        ? String(document.file_name || document.display_name || "")
                        : "";
                      const submission = [
                        document.submission_type,
                        document.submission_number,
                      ].filter(Boolean).join(" ");
                      return (
                        <tr key={`${url}-${index}`} className="hover:bg-muted/20">
                          <td className="px-2 py-2.5 whitespace-nowrap">
                            {String(document.review_category || document.document_type || "Review")}
                          </td>
                          <td className="px-2 py-2.5 max-w-[380px]">
                            <span title={String(document.title || displayName)}>
                              {displayName}
                            </span>
                            {submission && (
                              <span className="ml-2 text-muted-foreground">{submission}</span>
                            )}
                          </td>
                          <td className="px-2 py-2.5 whitespace-nowrap text-muted-foreground">
                            {formatFdaDate(document.date || document.submission_status_date)}
                          </td>
                          <td className="px-2 py-2.5 text-right whitespace-nowrap">
                            {url ? (
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                download={downloadName || undefined}
                                className="inline-flex items-center gap-1.5 text-primary hover:underline"
                              >
                                <FileText className="h-3.5 w-3.5" aria-hidden="true" />
                                {isPdf ? "打开 PDF" : "打开目录"}
                              </a>
                            ) : "-"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="mt-3 text-xs text-muted-foreground">
                FDA 当前未公开该申请的 Review 文件链接。
              </p>
            )}
          </section>
        );
      })}
    </div>
  );
}

function MarkdownTableRenderer({ content }: { content: string }) {
  if (!content || !content.includes("|") || !content.includes("---")) {
    return <pre className="mt-2 p-3 bg-muted/30 rounded text-xs overflow-x-auto whitespace-pre-wrap break-all">{content}</pre>;
  }
  const lines = content.trim().split("\n").filter(l => l.trim());
  if (lines.length < 2) return <pre className="mt-2 p-3 bg-muted/30 rounded text-xs overflow-x-auto whitespace-pre-wrap">{content}</pre>;
  const headerLine = lines[0];
  const headers = parseMarkdownRow(headerLine);
  const dataLines = lines.slice(2).filter((line) => line.includes("|"));
  const rows = dataLines.map((line) =>
    alignMarkdownRow(parseMarkdownRow(line), headers.length),
  );
  return (
    <div className="mt-3 overflow-x-auto rounded-lg border border-border">
      <table className="min-w-full text-xs">
        <thead>
          <tr className="bg-muted/50">
            {headers.map((h, i) => (
              <th key={i} className="px-3 py-2 text-left font-medium text-foreground whitespace-nowrap border-b border-border">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((row, ri) => (
            <tr key={ri} className="hover:bg-muted/20 transition-colors">
              {row.map((cell, ci) => (
                <td key={ci} className="px-3 py-2 text-muted-foreground whitespace-nowrap max-w-[200px] truncate" title={cell}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CdeReferenceSummary({ data }: { data: Record<string, unknown> }) {
  const referenceDrugs = Array.isArray(data.reference_drugs)
    ? data.reference_drugs as Record<string, unknown>[]
    : [];
  const markdownTable = typeof data.markdown_table === "string"
    ? data.markdown_table
    : "";

  return (
    <div className="mt-3">
      {referenceDrugs.length > 0 ? (
        <ReferenceDrugTable drugs={referenceDrugs} />
      ) : markdownTable && markdownTable.includes("|") ? (
        <MarkdownTableRenderer content={markdownTable} />
      ) : (
        <p className="text-sm text-muted-foreground">
          No displayable reference drug records.
        </p>
      )}
    </div>
  );
}

function DataRow({ label, value, highlight }: { label: string; value?: string; highlight?: boolean }) {
  if (!value) return null;
  if (value.includes("|") && value.includes("---") && value.includes("\n")) {
    return (
      <div className="data-row group">
        <span className="data-label">{label}</span>
        <MarkdownTableRenderer content={value} />
      </div>
    );
  }
  return (
    <div className="data-row group">
      <span className="data-label">{label}</span>
      <span className={`data-value ${highlight ? "text-primary font-medium" : ""}`}>
        {value}
      </span>
    </div>
  );
}

function LinkChip({ href, children }: { href: string; children: React.ReactNode }) {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="link mt-3"
    >
      {children}
      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
      </svg>
    </a>
  );
}

function uniqueJoined(values: unknown[]): string {
  return Array.from(
    new Set(values.map((value) => String(value || "").trim()).filter(Boolean)),
  ).join(" / ");
}

function getFdaStrengths(data: Record<string, unknown>): string {
  const products = Array.isArray(data.approval_products)
    ? data.approval_products as Record<string, unknown>[]
    : [];
  const selectedForm = String(data.dosage_form || "").toUpperCase();
  const matchingProducts = products.filter(
    (product) =>
      !selectedForm ||
      String(product.dosage_form || "").toUpperCase() === selectedForm,
  );

  return uniqueJoined(
    (matchingProducts.length > 0 ? matchingProducts : products).map((product) => {
      const strength = String(product.strength || "").trim();
      const status = String(product.marketing_status || "").toUpperCase();
      return status.includes("DISCONTINUED") && strength
        ? `${strength}（已停产）`
        : strength;
    }),
  );
}

interface PkMetric {
  parameter: string;
  notation?: string;
  value: string;
  unit?: string;
  qualifier?: string;
  context?: string;
  source_text?: string;
}

const PK_PARAMETER_LABELS: Record<string, string> = {
  Cmax: "峰浓度",
  AUC: "药时曲线下面积",
  tmax: "达峰时间",
  "t1/2": "消除半衰期",
  bioavailability: "绝对生物利用度",
  protein_binding: "血浆蛋白结合率",
  clearance: "清除率",
  Vd: "表观分布容积",
  steady_state: "达稳态时间",
  accumulation_ratio: "蓄积比",
};

function PkNotation({ metric }: { metric: PkMetric }) {
  switch (metric.parameter) {
    case "Cmax":
      return <><i>C</i><sub>max</sub></>;
    case "AUC": {
      const suffix = String(metric.notation || "")
        .replace(/^AUC/i, "")
        .replace(/[()]/g, "")
        .replace(/-/g, "–")
        .trim();
      return <><i>AUC</i>{suffix && <sub>{suffix}</sub>}</>;
    }
    case "tmax":
      return <><i>T</i><sub>max</sub></>;
    case "t1/2":
      return <><i>t</i><sub>1/2</sub></>;
    case "bioavailability":
      return <i>F</i>;
    case "protein_binding":
      return <i>PPB</i>;
    case "clearance":
      return <i>CL</i>;
    case "Vd":
      return <><i>V</i><sub>d</sub></>;
    case "steady_state":
      return <><i>t</i><sub>ss</sub></>;
    case "accumulation_ratio":
      return <><i>R</i><sub>acc</sub></>;
    default:
      return <i>{metric.notation || metric.parameter}</i>;
  }
}

function getCompactPk(data: Record<string, unknown>): string {
  if (!data.pk_parsed || typeof data.pk_parsed !== "object" || Array.isArray(data.pk_parsed)) {
    return "";
  }

  const pk = data.pk_parsed as Record<string, unknown>;
  const labels: Record<string, string> = {
    Cmax: "Cmax",
    AUC: "AUC",
    tmax: "Tmax",
    "t1/2": "T1/2",
    bioavailability: "生物利用度",
    protein_binding: "蛋白结合率",
    clearance: "清除率",
    Vd: "分布容积",
  };

  return Object.entries(labels)
    .map(([key, label]) => {
      const value = String(pk[key] || "").trim();
      return value ? `${label}: ${value}` : "";
    })
    .filter(Boolean)
    .join(" · ");
}

function safePkSummaryValue(value: unknown): string {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (!text || text === "—" || text === "-") return "";
  if (text.length > 160) return "";
  if (
    /(pharmacokinetics|clinical pharmacology|after a single|following oral|section\s*\d+|^\d+\.\d+\s+[A-Z][a-z]+)/i.test(text) &&
    text.length > 35
  ) {
    return "";
  }
  return text;
}

function formatPkMetricForSummary(metric: PkMetric): string {
  const value = safePkSummaryValue(metric.value);
  if (!value) return "";
  const qualifier = String(metric.qualifier || "").trim();
  const unit = String(metric.unit || "").trim();
  const prefix = qualifier ? `${qualifier} ` : "";
  const hasUnit = unit && value.toLowerCase().includes(unit.toLowerCase());
  return `${prefix}${value}${unit && !hasUnit ? ` ${unit}` : ""}`.trim();
}

function getSummaryPkMetric(metrics: PkMetric[], parameter: string): string {
  const values = metrics
    .filter((metric) => metric.parameter === parameter)
    .map((metric) => {
      const value = formatPkMetricForSummary(metric);
      if (!value) return "";
      const context = String(metric.context || "").trim();
      return context ? `${context}: ${value}` : value;
    })
    .filter(Boolean);

  return Array.from(new Set(values)).slice(0, 3).join("; ");
}

function buildSummaryPkParams(data?: Record<string, unknown>): SummaryPreviewCardProps["pkParams"] {
  const empty: SummaryPreviewCardProps["pkParams"] = {
    t12: "",
    bioavailability: "",
  };
  if (!data) return empty;

  const pkParsed =
    data.pk_parsed && typeof data.pk_parsed === "object" && !Array.isArray(data.pk_parsed)
      ? data.pk_parsed as Record<string, unknown>
      : {};
  const metrics = Array.isArray(data.pk_metrics)
    ? (data.pk_metrics as PkMetric[]).filter((metric) => metric && metric.parameter && metric.value)
    : [];
  const valueFrom = (parameter: string, parsedKey = parameter) =>
    getSummaryPkMetric(metrics, parameter) || safePkSummaryValue(pkParsed[parsedKey]);

  const summary: SummaryPreviewCardProps["pkParams"] = {
    cmax: valueFrom("Cmax"),
    auc: valueFrom("AUC"),
    tmax: valueFrom("tmax"),
    t12: valueFrom("t1/2"),
    bioavailability: valueFrom("bioavailability"),
    vd: valueFrom("Vd"),
    cl: valueFrom("clearance"),
    proteinBinding: valueFrom("protein_binding"),
  };

  const hasAnyValue = Object.entries(summary).some(
    ([key, value]) => key !== "notes" && Boolean(value),
  );
  const rawPkText = String(data.pk_parameters || "").trim();
  if (!hasAnyValue && rawPkText.length > 0) {
    summary.notes = "已获取 FDA 药代动力学原文，但未自动提取出可统计的关键数值；请在 FDA 数据源中查看完整原文。";
  }

  return summary;
}

function PkMetricsSummary({ data }: { data: Record<string, unknown> }) {
  const metrics = Array.isArray(data.pk_metrics)
    ? (data.pk_metrics as PkMetric[]).filter(
        (metric) => metric && metric.parameter && metric.value,
      )
    : [];

  if (metrics.length === 0) {
    const compactPk = getCompactPk(data);
    return compactPk
      ? <DataRow label="关键 PK 指标" value={compactPk} />
      : null;
  }

  const grouped = metrics.reduce<Map<string, PkMetric[]>>((result, metric) => {
    const current = result.get(metric.parameter) || [];
    current.push(metric);
    result.set(metric.parameter, current);
    return result;
  }, new Map());

  return (
    <div className="data-row group">
      <span className="data-label">关键 PK 指标</span>
      <div className="min-w-0 flex-1 overflow-hidden" data-testid="pk-metrics">
        <div className="overflow-x-auto rounded border border-border">
          <div className="min-w-[34rem] divide-y divide-border">
            {Array.from(grouped.entries()).map(([parameter, parameterMetrics]) => (
              <div
                key={parameter}
                data-testid={`pk-metric-${parameter}`}
                className="grid grid-cols-[minmax(10rem,0.9fr)_minmax(18rem,2fr)] gap-4 px-3 py-2.5"
              >
                <div className="flex items-baseline gap-2">
                  <span className="text-base font-semibold text-foreground">
                    <PkNotation metric={parameterMetrics[0]} />
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {PK_PARAMETER_LABELS[parameter] || parameter}
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-1.5">
                  {parameterMetrics.map((metric, index) => (
                    <span
                      key={`${metric.context || "general"}-${metric.value}-${index}`}
                      className="text-sm text-foreground"
                      title={metric.source_text || undefined}
                    >
                      {metric.context && (
                        <span className="text-muted-foreground">
                          {metric.context}:{" "}
                        </span>
                      )}
                      <span className="font-medium tabular-nums">
                        {metric.qualifier || ""}
                        {metric.value}
                      </span>
                      {metric.unit && <>{" "}{metric.unit}</>}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
          仅展示 FDA 标签中明确报告的数值和单位；标签未提供绝对{" "}
          <i>C</i><sub>max</sub> 或 <i>AUC</i> 时不作推算。
        </p>
      </div>
    </div>
  );
}

function FdaSummary({
  data,
  showSourceLink = true,
}: {
  data: Record<string, unknown>;
  showSourceLink?: boolean;
}) {
  const strengths = getFdaStrengths(data);

  return (
    <div className="mt-3">
      <DataRow label="品牌名" value={String(data.brand_name || "—")} highlight />
      <DataRow label="通用名" value={String(data.generic_name || "—")} />
      <DataRow label="生产企业" value={String(data.manufacturer || "—")} />
      <DataRow label="FDA 申请号" value={String(data.application_number || "—")} />
      <DataRow label="剂型 / 给药途径" value={uniqueJoined([data.dosage_form, data.route]) || "—"} />
      <DataRow label="匹配规格" value={String(data.approval_strength || "—")} highlight />
      <DataRow label="同剂型官方规格" value={strengths || "—"} />
      <PkMetricsSummary data={data} />
      {showSourceLink && data.openfda_url && (
        <LinkChip href={String(data.openfda_url)}>打开 openFDA 官方 Label 数据</LinkChip>
      )}
      <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
        页面仅展示检索摘要；适应症、药理、不良反应等完整内容请查看官方原文。
      </p>
    </div>
  );
}

function SummaryField({
  label,
  value,
  emphasize = false,
}: {
  label: string;
  value: unknown;
  emphasize?: boolean;
}) {
  const text = String(value || "").trim();
  if (!text) return null;
  return (
    <div className="min-w-0 py-1">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd
        className={`mt-1 break-words text-sm leading-relaxed ${
          emphasize ? "font-medium text-primary" : "text-foreground"
        }`}
      >
        {text}
      </dd>
    </div>
  );
}

function resolveDocumentHref(href: string): string {
  const value = String(href || "").trim();
  if (!value) return "";
  if (/^(?:https?:|mailto:|blob:|data:)/i.test(value)) return value;
  if (value.startsWith("/")) {
    return `${API_BASE.replace(/\/$/, "")}${value}`;
  }
  return value;
}

function OfficialDocumentLink({
  href,
  label,
}: {
  href: string;
  label: string;
}) {
  const resolvedHref = resolveDocumentHref(href);
  if (!resolvedHref) return null;
  const isCachedPdf =
    href.trim().startsWith("/api/local-file/") ||
    resolvedHref.includes("/api/local-file/");
  return (
    <a
      href={resolvedHref}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex min-h-9 items-center gap-2 rounded-md border border-border px-3 py-2 text-xs font-medium text-foreground transition-colors hover:border-primary/50 hover:bg-primary/5 hover:text-primary"
    >
      <FileText className="h-4 w-4 flex-none" aria-hidden="true" />
      <span className="break-words">{label}</span>
      <span
        className={`rounded-sm px-1.5 py-0.5 text-[11px] font-semibold ${
          isCachedPdf
            ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
            : "bg-muted text-muted-foreground"
        }`}
      >
        {isCachedPdf ? "本地 PDF" : "官方链接"}
      </span>
      <ExternalLink className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
    </a>
  );
}

function recordArray(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value)
    ? value.filter(
        (item): item is Record<string, unknown> =>
          Boolean(item) && typeof item === "object" && !Array.isArray(item),
      )
    : [];
}

function textList(value: unknown): string {
  return Array.isArray(value)
    ? uniqueJoined(value)
    : String(value || "").trim();
}

function markdownCell(value: unknown): string {
  const text = String(value ?? "").trim().replace(/\|/g, " / ");
  return text || "-";
}

function firstText(record: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = String(record[key] ?? "").trim();
    if (value) return value;
  }
  return "";
}

function buildCdeTrialsMarkdownTable(data: Record<string, unknown>): string {
  const registrations = recordArray(data.registrations);
  const trials = registrations.length > 0 ? registrations : recordArray(data.trials);
  if (trials.length === 0) return "";

  const header = [
    "序号",
    "登记号",
    "药物名称",
    "申请人",
    "试验状态",
    "公示日期",
    "试验类别",
    "周期",
    "临床研究机构",
    "入组人数",
  ];
  const lines = [
    `| ${header.join(" | ")} |`,
    `| ${header.map(() => "---").join(" | ")} |`,
  ];

  trials.slice(0, 20).forEach((trial, index) => {
    const cells = [
      String(index + 1),
      firstText(trial, ["reg_no", "registration_no", "trial_id"]),
      firstText(trial, ["drug_name", "drug"]),
      firstText(trial, ["applicant_name", "sponsor", "applicant"]),
      firstText(trial, ["trial_status", "status"]),
      firstText(trial, ["first_public_date", "registration_date", "public_date"]),
      firstText(trial, ["trial_category", "category"]),
      firstText(trial, ["period", "cycle"]),
      firstText(trial, [
        "clinical_institution",
        "clinical_institutions",
        "institution",
      ]),
      firstText(trial, ["planned_enrollment", "enrollment", "sample_size"]),
    ];
    lines.push(`| ${cells.map(markdownCell).join(" | ")} |`);
  });

  if (trials.length > 20) {
    lines.push("");
    lines.push(`*共 ${trials.length} 条记录，显示前 20 条*`);
  }

  return lines.join("\n");
}

const PMDA_DOSAGE_FORMS = [
  { marker: "口腔内崩壊錠", label: "口腔崩解片" },
  { marker: "OD錠", label: "口腔崩解片" },
  { marker: "錠", label: "片剂" },
  { marker: "カプセル", label: "胶囊" },
  { marker: "顆粒", label: "颗粒剂" },
  { marker: "細粒", label: "细粒剂" },
  { marker: "散", label: "散剂" },
  { marker: "ドライシロップ", label: "干糖浆剂" },
  { marker: "シロップ", label: "糖浆剂" },
  { marker: "液", label: "液体制剂" },
  { marker: "注", label: "注射剂" },
  { marker: "点滴", label: "注射剂" },
  { marker: "貼付", label: "贴剂" },
  { marker: "軟膏", label: "软膏剂" },
  { marker: "クリーム", label: "乳膏剂" },
  { marker: "点眼", label: "滴眼剂" },
  { marker: "点鼻", label: "鼻用制剂" },
  { marker: "吸入", label: "吸入制剂" },
];

function pickPmdaPackageInsert(product: Record<string, unknown>) {
  const links = recordArray(product.document_links);
  const direct = String(product.package_insert_url || "");
  const fromLinks = links.find((link) => {
    const label = String(link.label || "");
    const url = String(link.url || "");
    return (
      url.includes("ResultDataSetPDF") ||
      label.startsWith("PDF(") ||
      label.includes("添付文書")
    );
  });
  return {
    label: String(fromLinks?.label || "说明书 PDF"),
    url: direct || String(fromLinks?.url || ""),
  };
}

function pickPmdaReviewDocuments(product: Record<string, unknown>) {
  const links = recordArray(product.document_links);
  const direct = String(product.review_report_url || "");
  const docs = links
    .filter((link) => {
      const label = String(link.label || "");
      const url = String(link.url || "");
      return (
        url.toLowerCase().includes(".pdf") &&
        (label.includes("審査報告書") ||
          label.includes("审查报告") ||
          label.includes("審査") ||
          url.includes("/drugs/"))
      );
    })
    .map((link) => {
      const label = String(link.label || "").trim();
      return {
        label: label && /^\d+$/.test(label) ? `审评文件 ${label}` : label || "审评文件 PDF",
        url: String(link.url || ""),
      };
    });

  if (direct && !docs.some((doc) => doc.url === direct)) {
    docs.unshift({ label: "审评文件 PDF", url: direct });
  }

  return docs.filter((doc) => doc.url).slice(0, 4);
}

function getPmdaProductSegment(product: Record<string, unknown>): string {
  const description = String(product.description || "");
  const title = String(product.product_name || product.title || "").trim();
  if (!description) return "";

  let segment = description;
  if (title && segment.startsWith(title)) {
    segment = segment.slice(title.length).trim();
  }
  segment = segment
    .split(/製造販売元|販売元|PDF\(|HTML|XML|審査報告書|申請資料概要/)[0]
    .replace(/\s+/g, " ")
    .trim();

  return segment;
}

function parsePmdaVariantName(value: string) {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  const form = PMDA_DOSAGE_FORMS.find((item) => text.includes(item.marker));
  const specMatch = text.match(
    /(\d+(?:\.\d+)?\s*(?:mg|g|μg|µg|mcg|mL|ml|％|%)(?:\s*\/\s*\d+(?:\.\d+)?\s*(?:mg|g|μg|µg|mcg|mL|ml|％|%))?(?:分包|包|錠|カプセル|粒|瓶|袋)?)/i,
  );
  const specification = specMatch ? specMatch[1].replace(/\s+/g, "") : "";
  const productName = specification
    ? text.replace(specMatch?.[1] || "", "").trim()
    : text;

  return {
    productName: productName || text || "PMDA 药品",
    specification: specification || "请查看说明书",
    dosageForm: form ? `${form.marker}（${form.label}）` : "请查看说明书",
  };
}

function buildPmdaRows(product: Record<string, unknown>) {
  const productSegment = getPmdaProductSegment(product);
  const variants = productSegment
    ? productSegment
        .split(/[／/、,，;；]/)
        .map((item) => item.trim())
        .filter(Boolean)
    : [];
  const fallbackName = String(product.product_name || product.title || product.name || "PMDA 药品");
  const packageInsert = pickPmdaPackageInsert(product);
  const reviewDocuments = pickPmdaReviewDocuments(product);
  const sourceItems = variants.length > 0 ? variants : [fallbackName];

  return sourceItems.map((variant, index) => ({
    key: `${fallbackName}-${variant}-${index}`,
    ...parsePmdaVariantName(variant),
    packageInsert,
    reviewDocuments,
  }));
}

function PmdaProductsSummary({ data }: { data: Record<string, unknown> }) {
  const products = recordArray(data.products);

  if (products.length === 0) {
    return (
      <p className="mt-3 text-sm text-muted-foreground">
        PMDA 暂无可展示的药品记录。
      </p>
    );
  }

  const rows = products.flatMap(buildPmdaRows);

  return (
    <div className="mt-3">
      <div className="border-b border-border pb-3 text-xs text-muted-foreground">
        找到{" "}
        <strong className="text-foreground">
          {String(data.total_count ?? products.length)}
        </strong>{" "}
        条 PMDA 记录
        {data.search_term && (
          <span className="ml-3">
            检索词：<strong className="text-foreground">{String(data.search_term)}</strong>
          </span>
        )}
      </div>

      <div className="mt-3 overflow-x-auto rounded-md border border-border">
        <table className="min-w-[860px] w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
              <th className="w-12 px-3 py-2.5 font-medium">序号</th>
              <th className="px-3 py-2.5 font-medium">药品名称（商品名）</th>
              <th className="px-3 py-2.5 font-medium">规格</th>
              <th className="px-3 py-2.5 font-medium">剂型</th>
              <th className="px-3 py-2.5 font-medium">说明书</th>
              <th className="px-3 py-2.5 font-medium">审评文件</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/70">
            {rows.map((row, index) => (
              <tr key={row.key} className="hover:bg-muted/20">
                <td className="px-3 py-3 text-muted-foreground">{index + 1}</td>
                <td className="max-w-[260px] px-3 py-3 font-medium text-foreground">
                  <span className="break-words">{row.productName}</span>
                </td>
                <td className="px-3 py-3 font-mono text-xs text-foreground">
                  {row.specification}
                </td>
                <td className="px-3 py-3 text-muted-foreground">
                  {row.dosageForm}
                </td>
                <td className="px-3 py-3">
                  {row.packageInsert.url ? (
                    <a
                      href={row.packageInsert.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-primary hover:underline"
                    >
                      <FileText className="h-3.5 w-3.5" aria-hidden="true" />
                      打开说明书
                    </a>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </td>
                <td className="px-3 py-3">
                  {row.reviewDocuments.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {row.reviewDocuments.map((doc, docIndex) => (
                        <a
                          key={`${doc.url}-${docIndex}`}
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs text-foreground hover:border-primary/50 hover:text-primary"
                        >
                          <FileText className="h-3.5 w-3.5" aria-hidden="true" />
                          {doc.label || `审评${docIndex + 1}`}
                        </a>
                      ))}
                    </div>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
        仅展示 PMDA 查询所需的核心字段；完整原始数据和其他链接仍可通过复制或导出 CSV 查看。
      </p>
    </div>
  );
}

export function EmaProductsSummary({
  data,
}: {
  data: Record<string, unknown>;
}) {
  const products = recordArray(data.products);
  if (products.length === 0) {
    return (
      <p className="mt-3 text-sm text-muted-foreground">
        暂无可展示的EMA中央审批产品。
      </p>
    );
  }

  return (
    <div className="mt-3">
      <div className="border-b border-border pb-3 text-xs text-muted-foreground">
        找到{" "}
        <strong className="text-foreground">
          {String(data.total_count ?? products.length)}
        </strong>{" "}
        个EMA中央审批产品
      </div>
      <div className="divide-y divide-border">
        {products.map((product, index) => {
          const documents = recordArray(product.documents);
          const fallbackDocuments = [
            {
              label: "Assessment Report",
              url: product.assessment_report_url,
              local_url: product.assessment_report_local_url,
            },
            {
              label: "Product Information (SmPC/PL)",
              url: product.product_information_url || product.smpc_pdf_url,
              local_url: product.product_information_local_url,
            },
            {
              label: "EMA 药品主页",
              url: product.epar_url,
            },
          ];
          const visibleDocuments = (
            documents.length > 0 ? documents : fallbackDocuments
          )
            .map((document) => ({
              ...document,
              url: document.local_url || document.url,
            }))
            .filter((document) => String(document.url || "").trim());
          const specification =
            String(product.specification || "").trim() ||
            textList(product.strengths) ||
            "未在EMA清单中单列，请在 Product Information 中核对";
          const dosageForm =
            String(product.dosage_form || "").trim() ||
            textList(product.dosage_forms);

          return (
            <article
              key={String(
                product.ema_product_number ||
                  product.product_name ||
                  index,
              )}
              className="py-4 first:pt-3 last:pb-1"
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <h4 className="break-words text-base font-semibold text-foreground">
                    {String(
                      product.brand_name ||
                        product.product_name ||
                        "EMA 产品",
                    )}
                  </h4>
                  <p className="mt-1 break-words text-sm text-muted-foreground">
                    {String(
                      product.generic_name ||
                        product.active_substance ||
                        "活性成分未列出",
                    )}
                  </p>
                </div>
                {product.medicine_status && (
                  <span className="badge badge-success flex-none">
                    {String(product.medicine_status)}
                  </span>
                )}
              </div>

              <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
                <SummaryField
                  label="商品名"
                  value={product.brand_name || product.product_name}
                  emphasize
                />
                <SummaryField
                  label="药物名称 / 活性成分"
                  value={product.generic_name || product.active_substance}
                />
                <SummaryField
                  label="规格"
                  value={specification}
                  emphasize={Boolean(product.specification)}
                />
                <SummaryField label="剂型" value={dosageForm} />
                <SummaryField label="持证商" value={product.holder} />
                <SummaryField
                  label="EMA 产品编号"
                  value={product.ema_product_number}
                />
                <SummaryField
                  label="授权日期"
                  value={product.authorization_date}
                />
              </dl>

              {visibleDocuments.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {visibleDocuments.map((document, documentIndex) => (
                    <OfficialDocumentLink
                      key={`${String(document.url)}-${documentIndex}`}
                      href={String(document.url || "")}
                      label={String(
                        document.label || "EMA 官方文件",
                      )}
                    />
                  ))}
                </div>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
}

export function FdaPsgSummary({
  data,
}: {
  data: Record<string, unknown>;
}) {
  const summary =
    data.guidance_summary &&
    typeof data.guidance_summary === "object" &&
    !Array.isArray(data.guidance_summary)
      ? (data.guidance_summary as Record<string, unknown>)
      : data;
  const records = recordArray(data.psg_records);
  const documents = recordArray(data.guidance_documents);
  const fallbackUrls = Array.isArray(data.psg_official_urls)
    ? data.psg_official_urls
    : [data.psg_url];
  const visibleDocuments =
    documents.length > 0
      ? documents
      : fallbackUrls
          .filter(Boolean)
          .map((url) => ({
            label: "FDA Product-Specific Guidance",
            url,
          }));
  const recommendedStudies =
    summary.recommended_studies || summary.be_study_type;

  return (
    <div className="mt-3">
      <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
        <SummaryField
          label="有效成分"
          value={summary.active_ingredient || data.drug_name}
          emphasize
        />
        <SummaryField label="剂型" value={summary.dosage_form} />
        <SummaryField label="给药途径" value={summary.route} />
        <SummaryField
          label="推荐BE研究"
          value={recommendedStudies || "请查看FDA指导原则PDF"}
          emphasize
        />
        <SummaryField label="研究设计" value={summary.study_design} />
        <SummaryField label="参比制剂（RLD）" value={summary.rld_name} />
        <SummaryField label="样本量提示" value={summary.sample_size} />
        <SummaryField
          label="分析方法"
          value={summary.analytical_method}
        />
        <SummaryField label="主要PK指标" value={summary.pk_parameters} />
      </dl>

      {visibleDocuments.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2 border-t border-border pt-3">
          {visibleDocuments.map((document, index) => (
            <OfficialDocumentLink
              key={`${String(document.url)}-${index}`}
              href={String(document.url || "")}
              label={String(document.label || "打开FDA PSG PDF")}
            />
          ))}
        </div>
      )}

      {records.length > 1 && (
        <div className="mt-4 border-t border-border pt-3">
          <h4 className="text-xs font-medium text-muted-foreground">
            匹配到的指导原则版本
          </h4>
          <div className="mt-2 divide-y divide-border">
            {records.map((record, index) => (
              <div
                key={String(record.psg_url || index)}
                className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <p className="break-words text-sm font-medium text-foreground">
                    {uniqueJoined([
                      record.active_ingredient,
                      record.dosage_form,
                      record.route,
                    ]) || `指导原则 ${index + 1}`}
                  </p>
                  <p className="mt-1 break-words text-xs text-muted-foreground">
                    {uniqueJoined([
                      record.recommended_studies,
                      record.guidance_status,
                      record.guidance_date,
                    ])}
                  </p>
                </div>
                {record.psg_url && (
                  <a
                    href={String(record.psg_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex flex-none items-center gap-1.5 text-xs font-medium text-primary hover:underline"
                  >
                    <FileText className="h-4 w-4" aria-hidden="true" />
                    打开PDF
                    <ExternalLink
                      className="h-3.5 w-3.5"
                      aria-hidden="true"
                    />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CdeTrialsSummary({
  data,
  showSourceLink = true,
}: {
  data: Record<string, unknown>;
  showSourceLink?: boolean;
}) {
  const generatedTable = buildCdeTrialsMarkdownTable(data);
  const markdownTable =
    typeof data.markdown_table === "string" && data.markdown_table.includes("|")
      ? data.markdown_table
      : generatedTable;
  const total = data.source_total_count ?? data.total_count ?? data.trial_count;
  const detailed = data.fetched_detail_count ?? data.trial_count;

  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-border pb-3 text-xs">
        {total != null && (
          <span>
            <span className="text-muted-foreground">匹配记录 </span>
            <strong className="text-foreground">{String(total)}</strong>
          </span>
        )}
        {detailed != null && String(detailed) !== String(total) && (
          <span>
            <span className="text-muted-foreground">本次详情 </span>
            <strong className="text-foreground">{String(detailed)}</strong>
          </span>
        )}
      </div>
      {markdownTable ? (
        <MarkdownTableRenderer content={markdownTable} />
      ) : (
        <p className="py-4 text-sm text-muted-foreground">暂无可展示的临床试验表格。</p>
      )}
      {data.note && (
        <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
          {String(data.note)}
        </p>
      )}
      {showSourceLink && data.search_url && (
        <LinkChip href={String(data.search_url)}>打开 CDE 临床试验公示查询页</LinkChip>
      )}
    </div>
  );
}

function PubMedSummary({ data }: { data: Record<string, unknown> }) {
  const articles = recordArray(data.articles);
  const categoryCounts =
    data.category_counts &&
    typeof data.category_counts === "object" &&
    !Array.isArray(data.category_counts)
      ? Object.entries(data.category_counts as Record<string, unknown>)
      : [];

  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-border pb-3 text-xs">
        <span>
          <span className="text-muted-foreground">文献数 </span>
          <strong className="text-foreground">{String(data.count ?? articles.length)}</strong>
        </span>
        {data.search_strategy && (
          <span>
            <span className="text-muted-foreground">命中策略 </span>
            <strong className="text-foreground">{String(data.search_strategy)}</strong>
          </span>
        )}
        {categoryCounts.length > 0 && (
          <span className="text-muted-foreground">
            {categoryCounts.map(([label, count]) => `${label} ${String(count)}`).join(" · ")}
          </span>
        )}
      </div>

      {data.searched_aliases && (
        <p className="mt-2 text-xs text-muted-foreground">
          检索名称：{String(data.searched_aliases)}
        </p>
      )}

      {articles.length > 0 ? (
        <div className="mt-3 space-y-3">
          {articles.map((article, index) => {
            const tags = Array.isArray(article.tags)
              ? article.tags.map((tag) => String(tag)).filter(Boolean)
              : [];
            return (
              <article
                key={String(article.pmid || index)}
                className="rounded-md border border-border bg-card px-3 py-3"
              >
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  {tags.slice(0, 4).map((tag) => (
                    <span key={tag} className="badge badge-info">{tag}</span>
                  ))}
                  {article.year && <span>{String(article.year)}</span>}
                  {article.journal && <span>{String(article.journal)}</span>}
                </div>
                <h4 className="mt-2 text-sm font-semibold leading-relaxed text-foreground">
                  {String(article.title || "Untitled PubMed record")}
                </h4>
                {article.authors && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {String(article.authors)}
                  </p>
                )}
                {article.abstract && (
                  <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                    {String(article.abstract)}
                  </p>
                )}
                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                  {article.pmid && (
                    <span className="rounded-md bg-muted px-2 py-1 font-mono">
                      PMID {String(article.pmid)}
                    </span>
                  )}
                  {article.doi && (
                    <span className="rounded-md bg-muted px-2 py-1 font-mono">
                      DOI {String(article.doi)}
                    </span>
                  )}
                  {article.url && (
                    <OfficialDocumentLink
                      href={String(article.url)}
                      label="打开 PubMed"
                    />
                  )}
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <p className="py-4 text-sm text-muted-foreground">
          暂无可展示的 PubMed 文献。{data.note ? String(data.note) : ""}
        </p>
      )}
    </div>
  );
}

function MolecularFormula({ formula }: { formula: string }) {
  const tokens = Array.from(formula.matchAll(/([A-Z][a-z]?)(\d*)/g));
  if (tokens.length === 0) {
    return <span>{formula || "—"}</span>;
  }

  return (
    <span className="font-mono text-base font-semibold tracking-normal text-primary">
      {tokens.map((token, index) => (
        <span key={`${token[1]}-${index}`}>
          {token[1]}
          {token[2] && <sub className="text-[0.68em]">{token[2]}</sub>}
        </span>
      ))}
    </span>
  );
}

function PubChemSummary({ data }: { data: Record<string, unknown> }) {
  const compounds = recordArray(data.compounds);
  const isCombination = Boolean(data.is_combination);

  if (compounds.length === 0) {
    return (
      <div className="mt-3 rounded-md border border-border bg-muted/20 p-3 text-sm text-muted-foreground">
        PubChem 暂未返回可展示的分子结构记录。
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-4">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-border pb-3 text-xs">
        <span>
          <span className="text-muted-foreground">匹配成分 </span>
          <strong className="text-foreground">{String(data.compound_count ?? compounds.length)}</strong>
        </span>
        <span>
          <span className="text-muted-foreground">查询类型 </span>
          <strong className="text-foreground">{isCombination ? "复方，按活性成分拆分" : "单方"}</strong>
        </span>
        {data.normalized_query && (
          <span className="min-w-0">
            <span className="text-muted-foreground">标准化名称 </span>
            <strong className="break-words text-foreground">{String(data.normalized_query)}</strong>
          </span>
        )}
      </div>

      <div className="space-y-4">
        {compounds.map((compound, index) => {
          const formula = String(compound.molecular_formula || "");
          const analysis =
            compound.structure_analysis && typeof compound.structure_analysis === "object"
              ? (compound.structure_analysis as Record<string, unknown>)
              : {};
          const groups = recordArray(analysis.functional_groups);
          const formulaParts = recordArray(compound.formula_parts);
          const modelLinks = recordArray(compound.model_links);
          const imageUrl = String(compound.structure_2d_image || compound.structure_thumbnail || "");

          return (
            <article
              key={String(compound.cid || compound.query_component || index)}
              className="rounded-md border border-border bg-card p-4"
            >
              <div className="grid gap-4 lg:grid-cols-[minmax(14rem,18rem)_1fr]">
                <div className="rounded-md border border-border bg-white p-3 dark:bg-white">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={`${String(compound.query_component || "compound")} molecular structure`}
                      className="mx-auto aspect-square max-h-64 w-full object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex aspect-square items-center justify-center text-sm text-muted-foreground">
                      No structure image
                    </div>
                  )}
                </div>

                <div className="min-w-0">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h4 className="break-words text-base font-semibold text-foreground">
                        {String(compound.query_component || `Compound ${index + 1}`)}
                      </h4>
                      <p className="mt-1 text-xs text-muted-foreground">
                        PubChem CID {String(compound.cid || "—")}
                      </p>
                    </div>
                    {formula && <MolecularFormula formula={formula} />}
                  </div>

                  <div className="mt-3 grid gap-x-5 gap-y-2 sm:grid-cols-2">
                    <SummaryField label="分子量" value={compound.molecular_weight ? `${compound.molecular_weight} g/mol` : ""} emphasize />
                    <SummaryField label="IUPAC 名称" value={compound.iupac_name} />
                    <SummaryField label="InChIKey" value={compound.inchi_key} />
                    <SummaryField label="SMILES" value={compound.isomeric_smiles || compound.canonical_smiles} />
                    <SummaryField label="XLogP" value={compound.xlogp} />
                    <SummaryField label="TPSA" value={compound.tpsa ? `${compound.tpsa} A^2` : ""} />
                    <SummaryField label="氢键供体/受体" value={`${compound.h_bond_donor_count ?? "—"} / ${compound.h_bond_acceptor_count ?? "—"}`} />
                    <SummaryField label="可旋转键" value={compound.rotatable_bond_count} />
                  </div>

                  {formulaParts.length > 0 && (
                    <div className="mt-4">
                      <h5 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">元素组成</h5>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {formulaParts.map((part, partIndex) => (
                          <span
                            key={`${String(part.element)}-${partIndex}`}
                            className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/30 px-2 py-1 text-xs text-foreground"
                          >
                            <strong>{String(part.element)}</strong>
                            <span>{String(part.name_cn || part.name_en || "")}</span>
                            <span className="text-muted-foreground">x {String(part.count || 1)}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {groups.length > 0 && (
                    <div className="mt-4">
                      <h5 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">结构片段解析</h5>
                      <div className="mt-2 grid gap-2 sm:grid-cols-2">
                        {groups.map((group, groupIndex) => (
                          <div
                            key={`${String(group.key || group.name_en)}-${groupIndex}`}
                            className="rounded-md border border-border bg-muted/20 px-3 py-2"
                          >
                            <div className="text-sm font-medium text-foreground">
                              {String(group.name_cn || group.name_en || "结构片段")}
                            </div>
                            <div className="mt-1 text-xs leading-relaxed text-muted-foreground">
                              {String(group.description || "")}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap gap-2 border-t border-border pt-3">
                    {modelLinks.map((link, linkIndex) => (
                      <a
                        key={`${String(link.label || linkIndex)}-${linkIndex}`}
                        href={String(link.url || "")}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex min-h-8 items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-primary/50 hover:bg-primary/5 hover:text-primary"
                      >
                        {String(link.label || "Open model")}
                        <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {data.data_source_note && (
        <p className="text-xs leading-relaxed text-muted-foreground">
          {String(data.data_source_note)}
        </p>
      )}
    </div>
  );
}

function ThemeToggle() {
  const { theme, toggleTheme } = useTheme();
  return (
    <button
      onClick={toggleTheme}
      className="p-2 rounded-md hover:bg-muted transition-colors"
      title={theme === "light" ? "切换到深色模式" : "切换到浅色模式"}
    >
      {theme === "light" ? (
        <svg className="w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
      ) : (
        <svg className="w-5 h-5 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      )}
    </button>
  );
}

const QualityScoreRing = memo(({ score }: { score: number }) => {
  const color = score >= 90 ? "#22c55e" : score >= 80 ? "#eab308" : "#ef4444";
  const circumference = 2 * Math.PI * 20;
  const offset = circumference - (score / 100) * circumference;
  const label = score >= 90 ? "优秀" : score >= 80 ? "良好" : "需改进";

  return (
    <div className="flex items-center gap-3">
      <div className="relative w-14 h-14">
        <svg viewBox="0 0 50 50" className="w-full h-full -rotate-90">
          <circle cx="25" cy="25" r="20" fill="none" stroke="hsl(var(--muted))" strokeWidth="4" />
          <circle cx="25" cy="25" r="20" fill="none" stroke={color} strokeWidth="4"
            strokeDasharray={circumference} strokeDashoffset={offset}
            strokeLinecap="round" style={{ transition: "stroke-dashoffset 0.8s ease-out" }} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs font-bold" style={{ color }}>{score}</span>
        </div>
      </div>
      <div>
        <div className="text-sm font-bold text-foreground">{label}</div>
        <div className="text-xs text-muted-foreground">质量评分</div>
      </div>
    </div>
  );
});

export const DatasourcePanel = memo(({
  id,
  name,
  icon,
  status,
  duration,
  data,
  drugName,
  isExpanded,
  onToggle,
}: {
  id: string;
  name: string;
  icon: React.ReactNode;
  status: string;
  duration?: number;
  data?: Record<string, unknown>;
  drugName?: string;
  isExpanded: boolean;
  onToggle: () => void;
}) => {
  const isSuccess = status === "success";
  const isError = status === "error";
  const isNotFound = status === "not_found";
  const isProblem = isError || isNotFound;
  const isFdaReview = id === "fda_review";
  const isFda = id === "fda";
  const isFdaGuid = id === "fda_guid";
  const isEma = id === "ema";
  const isCdeTrials = id === "cde_trials";
  const isCdeRef = id === "cde_reflisted";
  const isPmda = id === "pmda";
  const isPubMed = id === "pubmed";
  const isPubChem = id === "pubchem";
  const sourceUrl = getSourceUrl(id, data);

  const localFilePath = drugName ? `sources/${id}/${drugName}.json` : null;

  const borderClass = isSuccess ? "border-l-success" : isProblem ? "border-l-destructive" : "border-l-muted-foreground/30";

  const handleOpenSource = useCallback(() => {
    if (!sourceUrl) return;
    window.open(sourceUrl, "_blank", "noopener,noreferrer");
  }, [sourceUrl]);

  const handleCopy = useCallback(async () => {
    if (!data) return;
    await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
  }, [data]);

  const handleExportCsv = useCallback(() => {
    if (!data) return;

    const blob = new Blob(["\uFEFF", buildCsv(data)], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${safeDownloadName(`${drugName || "query"}_${id}`)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }, [data, drugName, id]);

  return (
    <div className={`card overflow-hidden border-l-4 ${borderClass} transition-all duration-200`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <span className="text-lg" aria-hidden="true">{icon}</span>
          <span className="font-medium text-sm text-foreground">{name}</span>
          {isSuccess && <span className="badge badge-success">✓ 成功</span>}
          {isError && <span className="badge badge-danger">✗ 失败</span>}
          {isNotFound && <span className="badge badge-warning">∅ 无数据</span>}
          {duration !== undefined && (
            <span className="text-xs text-muted-foreground">{duration.toFixed(1)}s</span>
          )}
        </div>
        <span className="text-xs text-muted-foreground flex items-center gap-1">
          {isExpanded ? "收起 ▲" : "展开 ▼"}
        </span>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 animate-fadeIn border-t border-border">
          {data && Object.keys(data).length > 0 ? (
            isFdaReview ? (
              <div className="mt-3">
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-border pb-3 text-xs">
                  <span>
                    <span className="text-muted-foreground">FDA 申请 </span>
                    <strong className="text-foreground">{String(data.application_count ?? 0)}</strong>
                  </span>
                  <span>
                    <span className="text-muted-foreground">Review PDF </span>
                    <strong className="text-foreground">{String(data.target_pdf_count ?? data.review_pdf_count ?? 0)}</strong>
                  </span>
                </div>
                <FdaReviewDocuments review={data} />
              </div>
            ) : isFda ? (
              <FdaSummary data={data} showSourceLink={false} />
            ) : isFdaGuid ? (
              <FdaPsgSummary data={data} />
            ) : isEma ? (
              <EmaProductsSummary data={data} />
            ) : isCdeTrials ? (
              <CdeTrialsSummary data={data} showSourceLink={false} />
            ) : isCdeRef ? (
              <CdeReferenceSummary data={data} />
            ) : isPmda ? (
              <PmdaProductsSummary data={data} />
            ) : isPubMed ? (
              <PubMedSummary data={data} />
            ) : isPubChem ? (
              <PubChemSummary data={data} />
            ) : (
              <div className="mt-3 space-y-0">
                {Object.entries(data).map(([key, value]) => {
                  const displayLabel = key.replace(/_/g, ' ');
                  if (key === "reference_drugs" && Array.isArray(value) && value.length > 0) {
                    return (
                      <div key={key} className="data-row group">
                        <span className="data-label">{displayLabel}</span>
                        <ReferenceDrugTable drugs={value as Record<string, unknown>[]} />
                      </div>
                    );
                  }
                  if (key === "markdown_table" && typeof value === "string" && value.includes("|")) {
                    return (
                      <div key={key} className="data-row group">
                        <span className="data-label">{displayLabel}</span>
                        <MarkdownTableRenderer content={value} />
                      </div>
                    );
                  }
                  const formatted = formatDataForDisplay(value);
                  if (key === "reference_drugs" || key === "markdown_table") return null;
                  if (formatted && formatted.length > 500 && !formatted.includes("\n")) {
                    return (
                      <details key={key} className="data-row group cursor-pointer">
                        <summary className="flex items-center gap-1 hover:text-primary transition-colors">
                          <span className="data-label">{displayLabel}</span>
                          <span className="text-xs text-muted-foreground">({(formatted.length / 1024).toFixed(1)} KB, 点击展开)</span>
                        </summary>
                        <pre className="mt-2 p-3 bg-muted/30 rounded text-xs overflow-x-auto whitespace-pre-wrap break-all max-h-[300px] overflow-y-auto">{formatted}</pre>
                      </details>
                    );
                  }
                  return <DataRow key={key} label={displayLabel} value={formatted || "—"} />;
                })}
              </div>
            )
          ) : isError ? (
            <div className="mt-3 p-3 bg-destructive/5 rounded text-sm text-destructive">
              <p>错误详情: 数据源返回异常</p>
              <p className="text-xs mt-1 text-muted-foreground">建议: 稍后重试或检查网络连接</p>
            </div>
          ) : isNotFound ? (
            <div className="mt-3 p-3 bg-warning/5 rounded text-sm text-warning">
              该数据源未找到相关数据，可能药品未在该地区上市或数据尚未收录。
            </div>
          ) : (
            <div className="mt-3 p-3 text-sm text-muted-foreground">暂无详细数据</div>
          )}

          {localFilePath && isSuccess && !isFdaReview && !isFda && !isFdaGuid && !isEma && !isCdeTrials && !isCdeRef && !isPmda && !isPubMed && !isPubChem && (
            <div className="mt-3 pt-3 border-t border-border">
              <div className="local-file-info flex items-center gap-2 px-3 py-2 bg-emerald-500/5 rounded-md text-xs">
                <span>📁</span>
                <code className="text-emerald-700 dark:text-emerald-400 font-mono break-all">{localFilePath}</code>
                <span className="text-muted-foreground ml-auto">已保存至本地</span>
              </div>
            </div>
          )}

          <div className="flex gap-2 mt-3 pt-3 border-t border-border">
            <button
              type="button"
              className="btn btn-ghost text-xs h-7 gap-1 disabled:cursor-not-allowed disabled:opacity-40"
              onClick={handleOpenSource}
              disabled={!sourceUrl}
              title={sourceUrl ? "打开该数据源的官方原始页面" : "该数据源未返回原文链接"}
            >
              🔗 {isFdaReview ? "查看 FDA 申请页" : "查看原文"}
            </button>
            {!isFdaReview && (
              <button
                type="button"
                className="btn btn-ghost text-xs h-7 gap-1 disabled:cursor-not-allowed disabled:opacity-40"
                onClick={handleCopy}
                disabled={!data}
              >
                📋 复制
              </button>
            )}
            <button
              type="button"
              className="btn btn-ghost text-xs h-7 gap-1 disabled:cursor-not-allowed disabled:opacity-40"
              onClick={handleExportCsv}
              disabled={!data}
              title="导出当前数据源的查询结果"
            >
              📥 导出CSV
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

const DATASOURCE_KEY_MAP: Record<string, string> = {
  fda: "fda",
  fda_guid: "fda_guid",
  fda_review: "fda_review",
  ema: "ema",
  ich_m13a: "ich_m13a",
  pubmed: "pubmed",
  pubchem: "pubchem",
  cde_reflisted: "cde_reflisted",
  cde_trials: "cde_trials",
  nmpa: "nmpa",
  cde_yzxpj: "cde_yzxpj",
  dailymed: "dailymed",
};

const ResultRenderer = memo(({ result, selectedDatasources }: { result: NonNullable<TaskStatus["result"]>; selectedDatasources?: string[] }) => {
  const d = result.data || {};
  const fda   = d.fda   as Record<string, unknown> || {};
  const fdag  = d.fda_guid as Record<string, unknown> || {};
  const ema   = d.ema   as Record<string, unknown> || {};
  const pmda  = d.pmda  as Record<string, unknown> || {};
  const ich   = d.ich_m13a as Record<string, unknown> || {};
  const pub   = d.pubmed as Record<string, unknown> || {};
  const pubchem = d.pubchem as Record<string, unknown> || {};
  const cdeRef    = d.cde_reflisted as Record<string, unknown> || {};
  const cdeTrials = d.cde_trials as Record<string, unknown> || {};
  const nmpa      = d.nmpa as Record<string, unknown> || {};
  const cdeYzxpj  = d.cde_yzxpj as Record<string, unknown> || {};
  const dailymed  = d.dailymed as Record<string, unknown> || {};

  const isSelected = (dsId: string) => !selectedDatasources || selectedDatasources.length === 0 || selectedDatasources.includes(dsId);

  return (
    <div className="space-y-3">
      {isSelected("fda") && (
      <SectionCard title="FDA 药品信息" icon="🇺🇸" data={fda}>
        <FdaSummary data={fda} />
      </SectionCard>
      )}

      {isSelected("fda_guid") && (
      <SectionCard title="FDA 生物等效性指南（PSG）" icon="📋" data={fdag}>
        <FdaPsgSummary data={fdag} />
      </SectionCard>
      )}

      {isSelected("ema") && (
      <SectionCard title="EMA 欧洲药品管理局" icon="🇪🇺" data={ema}>
        <EmaProductsSummary data={ema} />
      </SectionCard>
      )}

      {isSelected("pmda") && pmda && Object.keys(pmda).length > 0 && (
      <SectionCard title="PMDA 日本药品医疗器械综合机构" icon="JP" data={pmda}>
        <PmdaProductsSummary data={pmda} />
      </SectionCard>
      )}

      {isSelected("ich_m13a") && (
      <SectionCard title="ICH M13A 低风险豁免" icon="🏆" data={ich}>
        <DataRow
          label="判断"
          value={ich.is_low_risk === true ? "✅ 低风险" : ich.is_low_risk === false ? "❌ 非低风险" : "⚠️ 需确认"}
          highlight={ich.is_low_risk === true}
        />
        <DataRow label="BCS 分类" value={String(ich.bcs_class || "—")} />
      </SectionCard>
      )}

      {isSelected("pubmed") && (
      <SectionCard title="PubMed 文献" icon="📚" data={pub}>
        <PubMedSummary data={pub} />
      </SectionCard>
      )}

      {isSelected("pubchem") && pubchem && Object.keys(pubchem).length > 0 && (
      <SectionCard title="PubChem 分子结构" icon={<Atom className="h-4 w-4" aria-hidden="true" />} data={pubchem}>
        <PubChemSummary data={pubchem} />
      </SectionCard>
      )}

      {isSelected("cde_reflisted") && cdeRef && Object.keys(cdeRef).length > 0 && (
        <SectionCard title="CDE 参比制剂目录" icon="🇨🇳" data={cdeRef}>
          {Array.isArray(cdeRef.reference_drugs) && cdeRef.reference_drugs.length > 0 ? (
            <>
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <span className="text-sm text-muted-foreground">
                  共 <strong className="text-foreground">{String(cdeRef.total_count ?? cdeRef.reference_drugs.length)}</strong> 条记录
                  {cdeRef.exact_count != null && (
                    <span className="ml-1">（严格匹配 <span className="text-success font-medium">{String(cdeRef.exact_count)}</span> 条）</span>
                  )}
                </span>
                {cdeRef.source_status && (
                  <div className="flex gap-1.5 ml-auto">
                    {Object.entries(cdeRef.source_status).map(([sk, sv]: [string, any]) => (
                      <span key={sk} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-muted/50 text-muted-foreground">
                        <span className={`w-1.5 h-1.5 rounded-full ${sv.status === "success" ? "bg-success" : "bg-warning"}`} />
                        {sk}: {sv.count ?? 0}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <ReferenceDrugTable drugs={cdeRef.reference_drugs as Record<string, unknown>[]} />
              {cdeRef.note && (
                <p className="mt-2 text-xs text-muted-foreground leading-relaxed">{String(cdeRef.note)}</p>
              )}
            </>
          ) : (
            <>
              <DataRow label="参比制剂名称" value={String(cdeRef.ref_drug_name || cdeRef.drug_name || "—")} highlight />
              <DataRow label="持证商" value={String(cdeRef.holder || "—")} />
              <DataRow label="规格" value={String(cdeRef.strength || cdeRef.specification || "—")} />
              <DataRow label="剂型" value={String(cdeRef.dosage_form || "—")} />
              <DataRow label="参考制剂类型" value={String(cdeRef.ref_type || cdeRef.source_type || "—")} />
            </>
          )}
        </SectionCard>
      )}

      {isSelected("cde_trials") && cdeTrials && Object.keys(cdeTrials).length > 0 && (
        <SectionCard title="CDE 临床试验" icon="🔬" data={cdeTrials}>
          <CdeTrialsSummary data={cdeTrials} />
        </SectionCard>
      )}

      {isSelected("nmpa") && nmpa && Object.keys(nmpa).length > 0 && (
        <SectionCard title="NMPA (国家药监局)" icon="🏥" data={nmpa}>
          <DataRow label="药品名称" value={String(nmpa.drug_name || "—")} highlight />
          <DataRow label="批准文号" value={String(nmpa.approval_number || "—")} />
          <DataRow label="生产企业" value={String(nmpa.manufacturer || "—")} />
          <DataRow label="规格" value={String(nmpa.specification || "—")} />
        </SectionCard>
      )}

      {isSelected("cde_yzxpj") && cdeYzxpj && Object.keys(cdeYzxpj).length > 0 && (
        <SectionCard title="CDE 一致性评价" icon="✅" data={cdeYzxpj}>
          <DataRow label="评价状态" value={String(cdeYzxpj.status || "—")} highlight />
          <DataRow label="受理号" value={String(cdeYzxpj.acceptance_no || "—")} />
          <DataRow label="申请人" value={String(cdeYzxpj.applicant || "—")} />
          <DataRow label="结论" value={String(cdeYzxpj.conclusion || "—")} />
        </SectionCard>
      )}

      {isSelected("dailymed") && dailymed && Object.keys(dailymed).length > 0 && (
        <SectionCard title="DailyMed 药品信息" icon="💊" data={dailymed}>
          <DataRow label="药品名称" value={String(dailymed.drug_name || "—")} highlight />
          <DataRow label="活性成分" value={String(dailymed.active_ingredient || "—")} />
          <DataRow label="剂型" value={String(dailymed.dosage_form || "—")} />
          <DataRow label="给药途径" value={String(dailymed.route || "—")} />
          {dailymed.label_url && <LinkChip href={String(dailymed.label_url)}>查看 DailyMed Label</LinkChip>}
        </SectionCard>
      )}
    </div>
  );
});

function SectionCard({ title, icon, children, data }: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  data?: Record<string, unknown>;
}) {
  const [expanded, setExpanded] = useState(true);
  const hasData = data && Object.keys(data).length > 0;

  return (
    <div className={`card overflow-hidden transition-all duration-300 ${hasData ? '' : 'opacity-60'}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full card-header flex items-center justify-between hover:bg-muted/40 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-2">
          {icon && <span className="text-base">{icon}</span>}
          <span className="section-title font-normal text-foreground">{title}</span>
          {!hasData && <span className="text-xs text-muted-foreground">(无数据)</span>}
        </div>
        <svg className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {expanded && (
        <div className="card-body animate-fadeIn">
          {children}
        </div>
      )}
    </div>
  );
}

function SkeletonLoader() {
  return (
    <div className="max-w-2xl mx-auto space-y-4 animate-fadeIn">
      <div className="skeleton h-32 w-full rounded-lg" />
      <div className="grid grid-cols-2 gap-4">
        <div className="skeleton h-10 w-full rounded" />
        <div className="skeleton h-10 w-full rounded" />
      </div>
      <div className="skeleton h-12 w-full rounded-lg" />
      <div className="grid grid-cols-3 gap-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="skeleton h-24 w-full rounded-lg" />
        ))}
      </div>
    </div>
  );
}

function AppContent() {
  const [stage, setStage] = useState<Stage>("submit");
  const [drugInput, setDrugInput] = useState("");
  const [selectedDrug, setSelectedDrug] = useState<DrugMapping | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [task, setTask] = useState<TaskStatus | null>(null);
  const [serverOk, setServerOk] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 新增：全局Loading和Error组件的状态
  const [showGlobalLoading, setShowGlobalLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState<'submitting' | 'querying' | 'processing' | 'generating' | 'downloading'>('submitting');
  const [structuredError, setStructuredError] = useState<Error | string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollFailureCountRef = useRef(0);

  const [selectedDosageForm, setSelectedDosageForm] = useState<DosageForm | null>(null);
  const [specification, setSpecification] = useState("");
  const [queryStartTime, setQueryStartTime] = useState<number | null>(null);
  const [dataSourceStatuses, setDataSourceStatuses] = useState<Array<{
    id: string; name: string; icon?: string;
    status: string; message?: string; elapsed?: number;
  }>>([]);
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);
  const [availableDatasources, setAvailableDatasources] = useState<DatasourceConfig[]>([]);
  const [dsLoadError, setDsLoadError] = useState<string | null>(null);
  const [dsRetrying, setDsRetrying] = useState(false);
  const [usingFallback, setUsingFallback] = useState(false);
  const [selectedDatasources, setSelectedDatasources] = useState<string[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [resultTab, setResultTab] = useState<ResultTab>("all");
  const [expandedResultPanels, setExpandedResultPanels] = useState<Set<string>>(new Set());
  const [elapsedTime, setElapsedTime] = useState(0);
  const elapsedRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { history, addHistory, removeHistory, clearHistory } = useQueryHistory();

  const specificationValidation = useMemo(
    () => validateSpecification(specification),
    [specification],
  );

  const canSubmit = Boolean(
    selectedDrug?.nameEn?.trim() &&
    selectedDosageForm &&
    selectedDatasources.length > 0 &&
    specificationValidation.isValid &&
    !loading
  );

  const enabledSourceSummary = useMemo(() => {
    const labels: Record<string, string> = {
      fda: "FDA",
      fda_guid: "FDA PSG",
      cde_reflisted: "CDE",
      cde_trials: "CDE",
      cde_yzxpj: "CDE",
      ema: "EMA",
      pmda: "PMDA",
      pubmed: "PubMed",
      pubchem: "PubChem",
      dailymed: "DailyMed",
      ich_m13a: "ICH M13A",
      nmpa: "NMPA",
      fda_review: "FDA Review",
    };
    const preferred = ["fda", "ema", "cde_reflisted", "pmda", "pubmed", "pubchem", "dailymed"];
    const enabledIds = availableDatasources.filter(ds => ds.enabled).map(ds => ds.id);
    const orderedIds = [
      ...preferred.filter(id => enabledIds.includes(id)),
      ...enabledIds.filter(id => !preferred.includes(id)),
    ];
    const uniqueLabels = Array.from(new Set(
      orderedIds.map(id => labels[id] || availableDatasources.find(ds => ds.id === id)?.name_en || id)
    ));
    return uniqueLabels.slice(0, 6).join(" · ") || "FDA · EMA · CDE · PubMed";
  }, [availableDatasources]);

  useEffect(() => {
    healthCheck().then(ok => setServerOk(ok)).catch(() => setServerOk(false));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchDsList = async () => {
      setDsRetrying(true);
      try {
        const data: DatasourcesResponse = await fetchDatasources();
        if (!cancelled) {
          setAvailableDatasources(data.datasources);
          setSelectedDatasources(data.datasources.filter(ds => ds.enabled).map(ds => ds.id));
          setDsLoadError(null);
          setUsingFallback(false);
        }
      } catch (err) {
        if (cancelled) return;
        const errMsg = err instanceof Error ? err.message : String(err);
        setDsLoadError(errMsg);
      } finally {
        if (!cancelled) setDsRetrying(false);
      }
    };
    fetchDsList();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    document.title = stage === "submit"
      ? "PharmaIntel - 智能药品查询"
      : stage === "progress"
        ? `PharmaIntel - 查询中... (${selectedDrug?.nameZh || ""})`
        : `PharmaIntel - ${task?.result?.drug_name || "查询结果"}`;
  }, [stage, selectedDrug?.nameZh, task?.result?.drug_name]);

  useEffect(() => {
    if (stage !== "progress" || !queryStartTime) return;
    elapsedRef.current = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - queryStartTime) / 1000));
    }, 1000);
    return () => {
      if (elapsedRef.current) clearInterval(elapsedRef.current);
    };
  }, [stage, queryStartTime]);

  const handleRetryDatasources = useCallback(async () => {
    setDsLoadError(null);
    setUsingFallback(false);
    setDsRetrying(true);
    try {
      const data: DatasourcesResponse = await fetchDatasources();
      setAvailableDatasources(data.datasources);
      setSelectedDatasources(data.datasources.filter(ds => ds.enabled).map(ds => ds.id));
      setDsLoadError(null);
    } catch (error) {
      const errMsg = error instanceof Error ? error.message : String(error);
      setDsLoadError(errMsg);
    } finally {
      setDsRetrying(false);
    }
  }, []);

  const handleUseFallback = useCallback(() => {
    setAvailableDatasources(FALLBACK_DATASOURCES);
    setSelectedDatasources(FALLBACK_DATASOURCES.filter(ds => ds.enabled).map(ds => ds.id));
    setDsLoadError(null);
    setUsingFallback(true);
  }, []);

  const findIconForDatasource = useCallback((dsId: string): string => {
    const ds = availableDatasources.find(d => d.id === dsId);
    return ds?.icon || '📊';
  }, [availableDatasources]);

  const handleCardToggle = useCallback((cardId: string) => {
    setExpandedCardId(prev => (prev === cardId ? null : cardId));
  }, []);

  const getDatasourceIcon = useCallback((dsId: string): React.ReactNode => {
    return DATASOURCE_ICONS[dsId] || findIconForDatasource(dsId);
  }, [findIconForDatasource]);

  const convertToProgressGridSources = useCallback((): ProgressGridSource[] => {
    return dataSourceStatuses.map(ds => ({
      id: ds.id,
      name: DATASOURCE_NAMES[ds.id] || ds.name,
      region: DATASOURCE_REGIONS[ds.id],
      icon: getDatasourceIcon(ds.id),
      status: ds.status as ProgressGridSource["status"],
      message: ds.message || '',
      duration: ds.elapsed,
    }));
  }, [dataSourceStatuses, getDatasourceIcon]);

  const toggleDatasource = useCallback((dsId: string) => {
    setSelectedDatasources(prev =>
      prev.includes(dsId) ? prev.filter(id => id !== dsId) : [...prev, dsId]
    );
  }, []);

  const selectAllDatasources = useCallback(() => {
    setSelectedDatasources(availableDatasources.filter(ds => ds.enabled).map(ds => ds.id));
  }, [availableDatasources]);

  const deselectAllDatasources = useCallback(() => {
    setSelectedDatasources([]);
  }, []);

  const validateForm = useCallback((): boolean => {
    const errors: FormErrors = {};
    if (!selectedDrug || !selectedDrug.nameEn.trim()) {
      errors.drugName = "请输入或选择一个有效的药品名称（至少2个字符）";
    }
    if (!selectedDosageForm) {
      errors.dosageForm = "请选择药品剂型";
    }
    if (!specificationValidation.isValid) {
      errors.specification = specificationValidation.error;
    }
    if (selectedDatasources.length === 0) {
      errors.datasources = "请至少选择一个数据源";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [selectedDrug, selectedDosageForm, selectedDatasources.length, specificationValidation]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) return;
    setError(null);
    setStructuredError(null);
    setLoading(true);

    // 显示全局Loading（新功能）
    setShowGlobalLoading(true);
    setLoadingStage('submitting');

    setTask(null);
    setTaskId(null);
    setQueryStartTime(Date.now());
    setElapsedTime(0);

    console.log("[Query] 提交查询:", {
      drug_name: selectedDrug?.nameEn,
      dosage_form: selectedDosageForm?.name,
      specification: specificationValidation.normalized || undefined,
      selected_datasources: selectedDatasources,
      source_count: selectedDatasources?.length,
    });

    if (availableDatasources.length > 0) {
      const initialStatuses = availableDatasources
        .filter(ds => selectedDatasources.includes(ds.id))
        .map(ds => ({
        id: ds.id,
        name: ds.name,
        icon: ds.icon,
        status: "pending" as const,
        message: "初始化中...",
      }));
      setDataSourceStatuses(initialStatuses);
    } else {
      setDataSourceStatuses([]);
    }

    if (pollRef.current) clearInterval(pollRef.current);

    try {
      const { task_id } = await submitQuery(
        selectedDrug!.nameEn,
        selectedDatasources,
        selectedDrug!.nameZh,
        selectedDosageForm?.code || selectedDosageForm?.name,
        specificationValidation.normalized || undefined,
      );
      setTaskId(task_id);
      setStage("progress");
      setLoadingStage('querying');  // 切换到查询阶段
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : "提交失败";
      setError(errorMessage);
      setStructuredError(e instanceof Error ? e : new Error(String(e)));
      setLoading(false);
      setShowGlobalLoading(false);  // 隐藏Loading
    }
  }, [selectedDrug, selectedDosageForm, specificationValidation, selectedDatasources, availableDatasources, validateForm]);

  const handleCancel = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    setLoading(false);
    setStage("submit");
    setError(null);
  }, []);

  const handleReset = useCallback(() => {
    setDrugInput("");
    setSelectedDrug(null);
    setSelectedDosageForm(null);
    setSpecification("");
    setTask(null);
    setTaskId(null);
    setLoading(false);
    setError(null);
    setDataSourceStatuses([]);
    setQueryStartTime(null);
    setElapsedTime(0);
    setFormErrors({});
    setResultTab("all");
    setExpandedResultPanels(new Set());
    setStage("submit");
  }, []);

  const handleQuickDownload = useCallback(async (type: string) => {
    if (!task?.result) return;
    try {
      const blob = await downloadReport(task.result.task_id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${task.result.drug_name}_report.${type === "sources" ? "zip" : "md"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("下载失败:", err);
    }
  }, [task?.result]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" && stage === "submit" && !showHistory) {
        e.preventDefault();
        handleSubmit();
      }
      if (e.key === "Escape") {
        if (showHistory) setShowHistory(false);
        else if (stage === "progress") handleCancel();
        else if (stage === "result") handleReset();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "d" && stage === "result") {
        e.preventDefault();
        handleQuickDownload("standard");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [stage, showHistory, handleSubmit, handleCancel, handleReset, handleQuickDownload]);

  const handleDrugSelect = (drug: DrugMapping) => {
    setSelectedDrug(drug);
    setSpecification("");
    if (formErrors.drugName) {
      setFormErrors(prev => ({ ...prev, drugName: undefined }));
    }
  };

  const handleDosageFormDetected = useCallback((result: DosageFormMatchResult) => {
    setSelectedDosageForm(result.dosageForm);
    setSpecification("");
    if (formErrors.dosageForm) {
      setFormErrors(prev => ({ ...prev, dosageForm: undefined }));
    }
  }, [formErrors.dosageForm]);

  useEffect(() => {
    if (!taskId) return;
    let cancelled = false;

    const scheduleNextPoll = (delay: number) => {
      if (cancelled) return;
      pollRef.current = setTimeout(runPoll, delay);
    };

    const runPoll = async () => {
      try {
        const t = await pollTask(taskId);
        pollFailureCountRef.current = 0;
        setError(null);
        setStructuredError(null);
        setTask(t);
        if (t.sources_progress && Array.isArray(t.sources_progress) && t.sources_progress.length > 0) {
          setDataSourceStatuses(t.sources_progress.map((src: DSProgressType) => ({
            id: src.id,
            name: src.name,
            icon: findIconForDatasource(src.id),
            status: src.status,
            message: src.message || '',
            elapsed: src.elapsed,
          })));
        }
        if (t.done || t.status === "ERROR") {
          if (pollRef.current) clearInterval(pollRef.current);
          setLoading(false);
          setShowGlobalLoading(false);  // 隐藏全局Loading
          if (t.done && t.result && selectedDrug && selectedDosageForm) {
            addHistory({
              drug_name: t.result.drug_name || selectedDrug.nameEn,
              dosage_form: selectedDosageForm.name,
              task_id: taskId,
              status: t.status === "ERROR" ? "ERROR" : "DONE",
              summary: t.message,
            });
            setTimeout(() => setStage("result"), 500);
          } else if (t.status === "ERROR") {
            setError(t.message || "查询失败");
            setTimeout(() => setStage("submit"), 1500);
          }
          return;
        }
        scheduleNextPoll(1000);
      } catch (err) {
        console.error('Polling error:', err);
        pollFailureCountRef.current += 1;
        const failureCount = pollFailureCountRef.current;
        const errorValue = err instanceof Error ? err : new Error(String(err));

        if (failureCount >= 60) {
          setError("长时间无法连接后端，但后台任务不会被取消。请确认后端仍在运行后重新打开查询历史。");
          setStructuredError(errorValue);
          setLoading(false);
          setShowGlobalLoading(false);
          return;
        }

        setTask((previous) =>
          previous
            ? {
                ...previous,
                message: `连接暂时中断，正在自动重连（第 ${failureCount} 次）；后台查询仍在继续`,
              }
            : previous,
        );
        const retryDelay = Math.min(1000 * (2 ** Math.min(failureCount - 1, 3)), 10000);
        scheduleNextPoll(retryDelay);
      }
    };

    runPoll();
    return () => {
      cancelled = true;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [taskId, findIconForDatasource, selectedDrug, selectedDosageForm, addHistory]);

  useEffect(() => {
    if (task?.status === "RUNNING" && task.message) {
      if (task.message.includes("AI") || task.message.includes("LLM")) {
        setLoadingStage('processing');
      } else if (task.message.includes("Word") || task.message.includes("报告")) {
        setLoadingStage('generating');
      } else {
        setLoadingStage('querying');
      }
    }
  }, [task?.status, task?.message]);

  const progressMessage = task?.status === "RUNNING"
    ? (task.message.includes("AI") || task.message.includes("LLM") ? "⏳ AI 整理中..."
      : task.message.includes("Word") || task.message.includes("报告") ? "⏳ 生成报告中..."
      : "⏳ 查询数据源中...")
    : null;

  const stats = useMemo(() => {
    const total = dataSourceStatuses.length;
    const success = dataSourceStatuses.filter(s => s.status === "success").length;
    const error = dataSourceStatuses.filter(s => s.status === "error").length;
    const notFound = dataSourceStatuses.filter(s => s.status === "not_found").length;
    const running = dataSourceStatuses.filter(s => s.status === "running").length;
    const pending = dataSourceStatuses.filter(s => s.status === "pending").length;
    const completed = success + error + notFound;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { total, success, error, notFound, running, pending, completed, percentage };
  }, [dataSourceStatuses]);

  const qualityScore = useMemo(() => {
    if (!dataSourceStatuses.length) return 0;
    const successRate = (stats.success / stats.total) * 100;
    const coverageBonus = Math.min(stats.success * 5, 20);
    return Math.min(Math.round(successRate * 0.7 + coverageBonus + 10), 100);
  }, [dataSourceStatuses.length, stats]);

  const formatTime = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m${s}s`;
  };

  const toggleResultPanel = (id: string) => {
    setExpandedResultPanels(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const filteredSourcesForTab = useMemo(() => {
    if (resultTab === "all") return dataSourceStatuses;
    if (resultTab === "success") return dataSourceStatuses.filter(s => s.status === "success");
    return dataSourceStatuses.filter(s => s.status === "error" || s.status === "not_found");
  }, [dataSourceStatuses, resultTab]);

  return (
    <div className="min-h-screen bg-background flex flex-col overflow-x-hidden">
      {/* 全局Loading覆盖层（新功能） */}
      {showGlobalLoading && (
        <GlobalLoading
          stage={loadingStage}
          progress={stats.percentage}
          elapsedTime={elapsedTime}
          estimatedRemaining={stats.total > 0 && stats.completed < stats.total ? Math.max(0, (stats.total - stats.completed) * 5) : undefined}
          onCancel={handleCancel}
          dataSourceStatuses={dataSourceStatuses.map(ds => ({
            name: ds.name,
            status: ds.status,
            message: ds.message
          }))}
        />
      )}

      {/* 用户友好的错误提示（新功能） */}
      {structuredError && !showGlobalLoading && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 w-full max-w-lg px-4">
          <UserFriendlyError
            error={structuredError}
            category={categorizeError(structuredError instanceof Error ? structuredError : new Error(String(structuredError)))}
            onRetry={() => {
              setStructuredError(null);
              setError(null);
              handleSubmit();
            }}
            onDismiss={() => {
              setStructuredError(null);
              setError(null);
            }}
            showDetails={true}
          />
        </div>
      )}
      {/* ===== Header ===== */}
      <header className="sticky top-0 z-40 h-[64px] bg-card/95 border-b border-border flex-shrink-0 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-card/90">
        <div className="max-w-content mx-auto h-full px-4 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 bg-primary/10 border border-primary/15 rounded-md flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 6h16M4 12h16M4 18h16" />
                  <circle cx="8" cy="6" r="2" fill="currentColor" />
                  <circle cx="16" cy="12" r="2" fill="currentColor" />
                  <circle cx="12" cy="18" r="2" fill="currentColor" />
                </svg>
              </div>
              <div className="hidden sm:block">
                <h1 className="text-base sm:text-lg font-semibold text-foreground tracking-tight leading-tight">
                  🧪 PharmaIntel
                </h1>
                <p className="text-[10px] sm:text-[11px] text-muted-foreground tracking-wide hidden md:block">Global Medicine Intelligence</p>
              </div>
            </div>
            {stage !== "submit" && (
              <div className="flex items-center gap-2 ml-2 pl-4 border-l border-border">
                <DrugChip drug={selectedDrug} />
                {selectedDosageForm && (
                  <span className="badge badge-info text-[10px] sm:text-xs hidden sm:inline-flex">
                    {selectedDosageForm.name}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <ThemeToggle />
            <div className="hidden sm:flex items-center gap-2 px-2.5 py-1 rounded-md bg-muted/70 border border-border">
              {serverOk === true && (
                <>
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-xs text-muted-foreground">已连接</span>
                </>
              )}
              {serverOk === false && (
                <>
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                  <span className="text-xs text-muted-foreground">未连接</span>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ===== Main Content ===== */}
      <main className="flex-1 max-w-content mx-auto px-4 sm:px-6 py-6 sm:py-8 w-full">
        {/* Data source error banner */}
        {dsLoadError && (
          <div className="max-w-2xl mx-auto mb-6 animate-slideUp">
            <div className="card border-destructive/30 bg-destructive/5 p-5">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                  <svg className="w-5 h-5 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-foreground">数据源配置加载失败</h3>
                  <p className="text-xs text-muted-foreground mt-1 break-all">{dsLoadError}</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button onClick={handleRetryDatasources} disabled={dsRetrying} className="btn btn-primary text-xs py-1.5 px-3">
                      {dsRetrying ? "重试中..." : "重新加载"}
                    </button>
                    <button onClick={handleUseFallback} className="btn text-xs py-1.5 px-3 border-border hover:bg-muted">
                      使用默认数据源（降级模式）
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {usingFallback && (
          <div className="max-w-2xl mx-auto mb-4 animate-fadeIn">
            <div className="card border-amber-500/30 bg-amber-500/5 p-3 flex items-center gap-2">
              <span className="text-amber-500 text-sm">⚠️</span>
              <span className="text-xs text-amber-700 dark:text-amber-400">
                当前运行在降级模式，数据源列表为预设默认值。
              </span>
              <button onClick={handleRetryDatasources} className="text-xs text-amber-600 dark:text-amber-400 underline ml-auto hover:no-underline">
                尝试恢复
              </button>
            </div>
          </div>
        )}

        {/* ==================== STAGE 1: Submit ==================== */}
        <div
          className={`transition-all duration-300 ease-out ${stage === "submit" ? "opacity-100 translate-y-0" : "opacity-0 absolute pointer-events-none -translate-y-4"}`}
          aria-hidden={stage !== "submit"}
        >
          {!showHistory ? (
            <div className="max-w-[880px] mx-auto space-y-6 animate-fadeIn">
              {/* Title */}
              <div className="text-center mb-8">
                <h2 className="text-2xl sm:text-3xl font-semibold text-foreground mb-3 tracking-tight">
                  🧪 PharmaIntel 智能药品查询
                </h2>
                <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                  输入药品名称，一键查询 {enabledSourceSummary} 等全球权威数据源
                </p>
              </div>

              {/* Search Card */}
              <div className="card p-5 sm:p-8 animate-slideUp">
                <div className="space-y-6">
                  {/* Drug Name Input */}
                  <div className="animate-slideUp stagger-1">
                    <label className="flex items-center gap-2 text-base font-semibold text-foreground mb-2.5">
                      <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      药品名称
                      <span className="text-destructive">*</span>
                    </label>
                    <DrugInputWithSuggestions
                      value={drugInput}
                        onChange={(value, drug) => {
                          setDrugInput(value);
                          if (drug) {
                            setSelectedDrug(toDrugMapping(drug));
                            setSpecification("");
                          if (formErrors.drugName) setFormErrors(prev => ({ ...prev, drugName: undefined }));
                        }
                      }}
                      onDrugSelect={handleDrugSelect}
                      onDosageFormDetected={handleDosageFormDetected}
                      placeholder="请输入药品名称（支持中英文，如：奥美拉唑/metformin）"
                    />
                    {formErrors.drugName && (
                      <p className="text-sm text-destructive mt-2 flex items-center gap-1.5 animate-scaleIn">
                        <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {formErrors.drugName}
                      </p>
                    )}
                    {selectedDrug && (
                      <div className="mt-2.5 flex items-center gap-3 text-base animate-fadeIn">
                        <div className="flex items-center gap-2 text-success">
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="font-medium">已选择</span>
                        </div>
                        <span className="text-foreground font-medium">
                          {selectedDrug.nameZh} ({selectedDrug.nameEn})
                        </span>
                        <span className="badge badge-info text-[10px]">{selectedDrug.category}</span>
                      </div>
                    )}
                  </div>

                  {/* Dosage Form & Specification */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-slideUp stagger-2">
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-foreground mb-2">
                        剂型
                        <span className="text-destructive">*</span>
                      </label>
                      <DosageFormDropdown
                        value={selectedDosageForm}
                        onChange={(df) => {
                          setSelectedDosageForm(df);
                          setSpecification("");
                          if (formErrors.dosageForm) setFormErrors(prev => ({ ...prev, dosageForm: undefined }));
                        }}
                        onClear={() => setSelectedDosageForm(null)}
                      />
                      {formErrors.dosageForm && (
                        <p className="text-xs text-destructive mt-1.5 flex items-center gap-1 animate-scaleIn">
                          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {formErrors.dosageForm}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-foreground mb-2">
                        规格
                        <span className="text-xs font-normal text-muted-foreground">（可选，手动输入）</span>
                      </label>
                      <SpecificationInput
                        drug={selectedDrug}
                        dosageForm={selectedDosageForm?.code || ''}
                        value={specification}
                        onChange={(value) => {
                          setSpecification(value);
                          if (formErrors.specification) {
                            setFormErrors(prev => ({ ...prev, specification: undefined }));
                          }
                        }}
                      />
                    </div>
                  </div>

                  {/* Datasource Selection */}
                  <div className="animate-slideUp stagger-3">
                    <div className="flex items-center justify-between mb-3.5">
                      <label className="flex items-center gap-2.5 text-base font-semibold text-foreground">
                        <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                        选择数据源
                        <span className="text-sm font-normal text-muted-foreground">(默认全选)</span>
                      </label>
                      <div className="flex gap-2.5">
                        <button onClick={selectAllDatasources} className="text-sm font-semibold text-primary hover:underline">全选</button>
                        <span className="text-border">|</span>
                        <button onClick={deselectAllDatasources} className="text-sm font-semibold text-primary hover:underline">清空</button>
                      </div>
                    </div>
                    {formErrors.datasources && (
                      <p className="text-sm text-destructive mb-2.5 flex items-center gap-1.5 animate-scaleIn">
                        <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {formErrors.datasources}
                      </p>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                      {availableDatasources.map(ds => {
                        const isSelected = selectedDatasources.includes(ds.id);

                        return (
                          <label
                            key={ds.id}
                            title={ds.name}
                            className={`datasource-item ${isSelected ? 'selected' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleDatasource(ds.id)}
                              className="sr-only"
                            />
                            <span className="ds-icon">
                              {ds.icon}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="ds-name">
                                {ds.name.split(' ')[0]}
                              </div>
                              <div className="ds-desc">
                                {ds.name.substring(ds.name.indexOf(' ') + 1) || '—'}
                              </div>
                            </div>
                            {isSelected && (
                              <div className="ds-check">
                                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 pt-2 animate-slideUp stagger-4">
                    <button
                      onClick={handleSubmit}
                      disabled={!canSubmit}
                      className="btn btn-primary w-full sm:w-auto text-base h-12 px-8 font-semibold gap-2 active:scale-[0.98]"
                    >
                      {loading ? (
                        <>
                          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25"/><path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                          提交中...
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          开始查询
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => setShowHistory(!showHistory)}
                      className="btn btn-secondary w-full sm:w-auto gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      📜 查询历史
                    </button>
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 px-4 py-3 rounded animate-scaleIn">
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {error}
                    </div>
                  )}
                </div>
              </div>

              <p className="text-center text-sm text-muted-foreground animate-fadeIn pt-3">
                数据来源：{enabledSourceSummary} 等 · 仅供信息汇总，不构成医疗建议
              </p>
            </div>
          ) : (
            /* History Panel */
            <div className="max-w-3xl mx-auto animate-fadeIn">
              <div className="flex items-center gap-3 mb-4">
                <button
                  onClick={() => setShowHistory(false)}
                  className="btn btn-ghost h-8 gap-1"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                  返回
                </button>
                <h2 className="text-xl font-bold text-foreground">📜 查询历史</h2>
              </div>
              <QueryHistoryList
                history={history}
                onReQuery={(item) => {
                  const matched = drugMappings.find(
                    (d) => d.nameEn === item.drug_name || d.nameZh === item.drug_name
                  );
                  if (matched) {
                    setSelectedDrug(matched);
                    setDrugInput(matched.nameZh);
                    const df = DOSAGE_FORMS.find((f) => f.name === item.dosage_form);
                    if (df) setSelectedDosageForm(df);
                    setShowHistory(false);
                    setTimeout(() => handleSubmit(), 100);
                  }
                }}
                onDelete={removeHistory}
                onClear={clearHistory}
              />
            </div>
          )}
        </div>

        {/* ==================== STAGE 2: Progress ==================== */}
        <div
          className={`transition-all duration-300 ease-out ${stage === "progress" ? "opacity-100 translate-y-0" : "opacity-0 absolute pointer-events-none translate-y-4"}`}
          aria-hidden={stage !== "progress"}
        >
          <div className="max-w-[1100px] mx-auto space-y-4 animate-fadeIn">
            {/* Status Header */}
            <div className="card p-4 flex items-center justify-between animate-slideDown">
              <div className="flex items-center gap-3">
                <button
                  onClick={handleCancel}
                  className="btn btn-ghost h-8 gap-1 text-sm"
                  title="返回修改 (Esc)"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                  返回
                </button>
                <div className="h-5 w-px bg-border" />
                <DrugChip drug={selectedDrug} />
                {selectedDosageForm && (
                  <span className="badge badge-info text-xs">{selectedDosageForm.name}</span>
                )}
              </div>
              <button
                onClick={handleCancel}
                className="btn btn-ghost h-8 gap-1 text-sm text-destructive hover:bg-destructive/10"
                title="取消查询 (Esc)"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                取消
              </button>
            </div>

            {/* Total Progress Bar */}
            <div className="card p-5 animate-slideUp" style={{ animationDelay: "100ms" }}>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">总体进度</span>
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-primary">{stats.percentage}%</span>
                    <span className="text-sm text-muted-foreground">已用 {formatTime(elapsedTime)}</span>
                  </div>
                </div>
                <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all duration-700 ease-out relative"
                    style={{ width: `${stats.percentage}%` }}
                  >
                    <div className="absolute inset-0 bg-white/20 animate-pulse" />
                  </div>
                </div>
                {stats.running > 0 && (
                  <p className="text-xs text-muted-foreground">
                    💡 正在解析各国药品数据并生成报告...
                    {stats.total > 0 && stats.completed < stats.total && (
                      <> · 预估剩余 ~{Math.max(0, Math.round((stats.total - stats.completed) * 5))}s</>
                    )}
                  </p>
                )}
              </div>
            </div>

            {/* Progress Grid */}
            <ProgressGrid
              sources={convertToProgressGridSources()}
              expandedCardId={expandedCardId}
              onCardToggle={handleCardToggle}
            />

            {/* Stats Footer */}
            <div className="card p-4 animate-slideUp" style={{ animationDelay: "200ms" }}>
              <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-sm">
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                  <span className="text-muted-foreground">成功:</span>
                  <strong className="text-success">{stats.success}</strong>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                  <span className="text-muted-foreground">失败:</span>
                  <strong className="text-destructive">{stats.error}</strong>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                  <span className="text-muted-foreground">无数据:</span>
                  <strong className="text-warning">{stats.notFound}</strong>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-blue-500 animate-pulse" />
                  <span className="text-muted-foreground">查询中:</span>
                  <strong className="text-info">{stats.running}</strong>
                </span>
                <span className="text-muted-foreground">
                  共 <strong>{stats.total}</strong> 个数据源
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ==================== STAGE 3: Result ==================== */}
        <div
          className={`transition-all duration-300 ease-out ${stage === "result" ? "opacity-100 translate-y-0" : "opacity-0 absolute pointer-events-none translate-y-4"}`}
          aria-hidden={stage !== "result"}
        >
          {task?.done && task.result && (
            <div className="max-w-[1200px] mx-auto space-y-4 animate-fadeIn">
              {/* Sticky Summary Banner */}
              <div className="sticky top-[64px] z-30 card p-4 sm:p-5 animate-slideDown">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className="w-8 h-8 rounded-md bg-success/10 border border-success/20 flex items-center justify-center">
                        <svg className="w-5 h-5 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </span>
                      <div>
                        <span className="font-semibold text-foreground text-sm sm:text-base">查询完成</span>
                        <span className="text-muted-foreground text-xs sm:text-sm ml-2">
                          用时: {formatTime(elapsedTime)}
                        </span>
                      </div>
                    </div>
                    <QualityScoreRing score={qualityScore} />
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={handleSubmit}
                      className="btn btn-secondary text-xs h-8 gap-1"
                      title="重新查询"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      重查
                    </button>
                    <DownloadPanel
                      taskId={task.result.task_id}
                      drugName={task.result.drug_name}
                      hasLlmReport={!!task.result.report_text && task.result.llm_status === "完成"}
                      onDownloadStart={(type) => console.log(`[Download] Started: ${type}`)}
                      onDownloadComplete={(type) => console.log(`[Download] Complete: ${type}`)}
                      onError={(err) => console.error(`[Download] Error:`, err)}
                    />
                    <button
                      onClick={handleReset}
                      className="btn btn-ghost text-xs h-8 gap-1"
                      title="返回首页"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                      </svg>
                      首页
                    </button>
                  </div>
                </div>
              </div>

              {/* Result Tabs */}
              <div className="card p-1 inline-flex gap-1 rounded-md bg-muted/30">
                {([
                  { key: "all" as const, label: "全部结果", count: dataSourceStatuses.length },
                  { key: "success" as const, label: "✅ 成功", count: stats.success },
                  { key: "issues" as const, label: "❌ 问题", count: stats.error + stats.notFound },
                ]).map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setResultTab(tab.key)}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-150 ${
                      resultTab === tab.key
                        ? "bg-card text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    }`}
                  >
                    {tab.label}
                    {tab.count > 0 && (
                      <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                        tab.key === "success" ? "bg-success/15 text-success" :
                        tab.key === "issues" ? "bg-destructive/15 text-destructive" :
                        "bg-muted-foreground/15 text-muted-foreground"
                      }`}>
                        {tab.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Summary Preview */}
              <SummaryPreviewCard
                drugName={{
                  cn: task.result.drug_name,
                  en: String((task.result.data?.fda as Record<string, unknown>)?.generic_name || task.result.drug_name),
                  inn: String((task.result.data?.fda as Record<string, unknown>)?.generic_name || ""),
                }}
                dosageForm={String((task.result.data?.fda as Record<string, unknown>)?.dosage_form || selectedDosageForm?.name || "—")}
                pkParams={buildSummaryPkParams(task.result.data?.fda as Record<string, unknown> | undefined)}
                sourceCoverage={{ covered: stats.success + stats.notFound, total: stats.total }}
                qualityScore={qualityScore}
                onDownload={async () => {
                  if (!task.result) return;
                  const blob = await downloadReport(task.result.task_id);
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `${task.result.drug_name}_report.docx`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
              />

              {/* Datasource Panels */}
              <div className="space-y-3">
                {filteredSourcesForTab.map(ds => {
                  const dsKey = ds.id;
                  return (
                    <DatasourcePanel
                      key={dsKey}
                      id={dsKey}
                      name={DATASOURCE_NAMES[dsKey] || ds.name}
                      icon={getDatasourceIcon(dsKey)}
                      status={ds.status}
                      duration={ds.elapsed}
                      data={(task.result?.data as Record<string, Record<string, unknown>>)?.[dsKey]}
                      drugName={task.result?.drug_name}
                      isExpanded={expandedResultPanels.has(dsKey)}
                      onToggle={() => toggleResultPanel(dsKey)}
                    />
                  );
                })}
              </div>

              {/* Detailed Results */}
              <div className="pt-2">
                <ResultRenderer result={task.result} selectedDatasources={selectedDatasources} />
              </div>
            </div>
          )}
        </div>
      </main>

      {/* ===== Footer ===== */}
      <footer className="border-t border-border bg-card/70 py-4 mt-auto flex-shrink-0">
        <div className="max-w-content mx-auto px-4 sm:px-6 text-center text-xs text-muted-foreground">
          PharmaIntel v0.1 · 智能医药情报平台 · 仅供信息汇总，不构成医疗建议
        </div>
      </footer>

      {/* Mobile Bottom Nav */}
      <nav className="sm:hidden fixed bottom-0 left-0 right-0 h-[60px] bg-card border-t border-border shadow-[0_-2px_10px_rgba(0,0,0,0.08)] flex items-center justify-around z-50">
        <button
          onClick={() => { handleReset(); }}
          className={`flex flex-col items-center gap-0.5 min-w-[44px] min-h-[44px] justify-center ${stage === "submit" ? "text-primary" : "text-muted-foreground"}`}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <span className="text-[10px]">查询</span>
        </button>
        <button
          onClick={() => { if (stage === "submit" && canSubmit) handleSubmit(); }}
          className={`flex flex-col items-center gap-0.5 min-w-[44px] min-h-[44px] justify-center ${stage === "progress" ? "text-primary" : "text-muted-foreground"}`}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <span className="text-[10px]">进度</span>
        </button>
        <button
          className={`flex flex-col items-center gap-0.5 min-w-[44px] min-h-[44px] justify-center ${stage === "result" ? "text-primary" : "text-muted-foreground"}`}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span className="text-[10px]">结果</span>
        </button>
      </nav>
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}
